import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, Users, Building2, Scale, BadgeIndianRupee, Home as HomeIcon,
    MessageSquare, Star, Settings, ShieldCheck, Trash2, Search, BadgeCheck, ChevronRight, X, Plus, Edit3, Gavel, Clock, FileCheck2, Wallet, LifeBuoy, Check, Send, AlertTriangle, Wrench, Eye, EyeOff, Store, PencilRuler, Paintbrush, HardHat, Calculator, Hammer, User as UserIcon, Loader2, Video, Handshake, MoreVertical,
} from "lucide-react";
import {
    BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { api, formatINR, fileUrl, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UserFormModal, PropertyFormModal } from "../components/AdminModals";
import AdminReels from "./AdminReels";

/** Complete list of role categories shown as filter tabs in the Admin Panel.
 *  Each category maps to one or more `role` values on the user document, so a new
 *  registration automatically lands in the correct bucket. `All Users` counts every
 *  unique user exactly once regardless of role. Legacy `user` / `buyer` roles are
 *  grouped under the "Buyer / Tenant" bucket. */
const USER_CATEGORIES = [
    { id: "cat_buyer",             label: "Buyer / Tenant",          roles: ["user", "buyer"],           icon: UserIcon },
    { id: "cat_seller",            label: "Seller / Owner",          roles: ["seller"],                  icon: Store },
    { id: "cat_broker",            label: "Broker",                  roles: ["broker"],                  icon: BadgeCheck },
    { id: "cat_advocate",          label: "Advocate",                roles: ["advocate"],                icon: Scale },
    { id: "cat_loan_agent",        label: "Loan Agent",              roles: ["loan_agent"],              icon: BadgeIndianRupee },
    { id: "cat_architect",         label: "Architect",               roles: ["architect"],               icon: PencilRuler },
    { id: "cat_interior_designer", label: "Interior Designer",       roles: ["interior_designer"],       icon: Paintbrush },
    { id: "cat_civil_contractor",  label: "Civil Contractor",        roles: ["civil_contractor"],        icon: HardHat },
    { id: "cat_civil_engineer",    label: "Civil Engineer",          roles: ["civil_engineer"],          icon: Wrench },
    { id: "cat_property_valuer",   label: "Property Valuer",         roles: ["property_valuer"],         icon: Calculator },
    { id: "cat_renovation",        label: "Renovation",              roles: ["renovation"],              icon: Hammer },
    { id: "cat_builder",           label: "Builder / Developer",     roles: ["builder"],                 icon: Building2 },
];

const NAV = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "users", label: "All Users", icon: Users },
    // Category tabs (12) — see USER_CATEGORIES. Rendered as a nested section in the sidebar.
    ...USER_CATEGORIES,
    { id: "service_pros", label: "Service Pros (All)", icon: Wrench },
    { id: "kyc", label: "KYC Review", icon: FileCheck2 },
    { id: "properties", label: "Properties", icon: Building2 },
    { id: "featured_requests", label: "Featured Requests", icon: Star },
    { id: "reels", label: "Reels Moderation", icon: Video },
    { id: "gpm_partners", label: "GPM Partners", icon: Handshake },
    { id: "listing_credits", label: "Listing Credits (₹99)", icon: BadgeIndianRupee },
    { id: "broadcasts", label: "Broadcasts", icon: Send },
    { id: "auctions", label: "Auctions", icon: Gavel },
    { id: "inquiries", label: "Inquiries", icon: MessageSquare },
    { id: "support", label: "Support Messages", icon: LifeBuoy },
    { id: "reviews", label: "Reviews", icon: Star },
    { id: "withdrawals", label: "Withdrawals", icon: Wallet },
    { id: "settings", label: "Settings", icon: Settings },
];

const GOLD = "#D4AF37";
const PIE_COLORS = ["#D4AF37", "#F3E5AB", "#B8951F", "#E6CB6F", "#8B6F18"];

export default function AdminDashboard() {
    const { user } = useAuth();
    const [active, setActive] = useState("overview");
    const [sidebarOpen, setSidebarOpen] = useState(false);
    // Live per-role counts + total unique users — computed from a single fetch so all
    // sidebar badges stay in sync with reality (no hardcoded numbers).
    const [roleCounts, setRoleCounts] = useState({});
    const [totalUsers, setTotalUsers] = useState(0);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/admin/users");
                if (cancelled) return;
                const list = Array.isArray(data) ? data : [];
                const counts = {};
                list.forEach((u) => { const r = u.role || "unknown"; counts[r] = (counts[r] || 0) + 1; });
                setRoleCounts(counts);
                setTotalUsers(list.length);
            } catch { /* silent */ }
        };
        load();
        const t = setInterval(load, 30000);
        return () => { cancelled = true; clearInterval(t); };
    }, []);

    const countFor = (n) => {
        if (n.id === "users") return totalUsers;
        if (n.id === "service_pros") return ["loan_agent","advocate","architect","interior_designer","civil_engineer","civil_contractor","property_valuer","renovation"].reduce((s, r) => s + (roleCounts[r] || 0), 0);
        if (n.roles) return n.roles.reduce((s, r) => s + (roleCounts[r] || 0), 0);
        return null;
    };

    return (
        <div className="min-h-screen bg-navy-950 flex">
            {/* SIDEBAR */}
            <aside
                className={`fixed lg:sticky top-20 left-0 z-40 h-[calc(100vh-5rem)] w-64 bg-navy-900 border-r border-white/5 transition-transform overflow-y-auto ${
                    sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
                }`}
                data-testid="admin-sidebar"
            >
                <div className="p-6 border-b border-white/5 hidden lg:block">
                    <div className="label-overline mb-1">Admin Console</div>
                    <div className="font-heading font-bold text-white truncate">{user?.name}</div>
                    <div className="text-[11px] text-gold-500 mt-0.5">Super Administrator</div>
                </div>
                <nav className="p-3 space-y-1">
                    {NAV.map((n) => {
                        const Icon = n.icon;
                        const isActive = active === n.id;
                        const count = countFor(n);
                        return (
                            <button
                                key={n.id}
                                data-testid={`admin-nav-${n.id}`}
                                onClick={() => { setActive(n.id); setSidebarOpen(false); }}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all ${
                                    isActive
                                        ? "bg-gold-500/10 border-l-2 border-gold-500 text-gold-500 font-semibold"
                                        : "text-white/70 hover:text-white hover:bg-navy-800 border-l-2 border-transparent"
                                }`}
                            >
                                <Icon className="w-4 h-4 shrink-0" />
                                <span className="flex-1 text-left truncate">{n.label}</span>
                                {count !== null && count !== undefined && (
                                    <span
                                        data-testid={`admin-nav-count-${n.id}`}
                                        className={`min-w-[22px] px-1.5 py-0.5 rounded-full text-[10px] font-extrabold text-center ${
                                            isActive ? "bg-gold-500 text-navy-950" : "bg-white/10 text-white/80"
                                        }`}
                                    >
                                        {count}
                                    </span>
                                )}
                                {isActive && count === null && <ChevronRight className="w-3 h-3 ml-auto" />}
                            </button>
                        );
                    })}
                </nav>
            </aside>

            {/* MOBILE OVERLAY */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
            )}

            {/* MAIN */}
            <div className="flex-1 min-w-0">
                <div className="lg:hidden p-4 border-b border-white/5 bg-navy-900 sticky top-20 z-20 flex items-center justify-between">
                    <div>
                        <div className="label-overline">Admin</div>
                        <div className="font-heading font-bold text-white capitalize">{NAV.find((n) => n.id === active)?.label}</div>
                    </div>
                    <button onClick={() => setSidebarOpen(true)} className="px-3 py-1.5 rounded-md bg-gold-500 text-navy-900 text-xs font-semibold" data-testid="admin-mobile-toggle">Menu</button>
                </div>

                <div className="p-6 lg:p-10 max-w-[1400px]" data-testid="admin-content">
                    {active === "overview" && <Overview />}
                    {active === "users" && <UsersTable filter={null} />}
                    {USER_CATEGORIES.some((c) => c.id === active) && (() => {
                        const cat = USER_CATEGORIES.find((c) => c.id === active);
                        return <UsersTable filter={cat.roles} filterTitle={cat.label} />;
                    })()}
                    {active === "service_pros" && <UsersTable filter={["loan_agent", "advocate", "architect", "interior_designer", "civil_engineer", "civil_contractor", "property_valuer", "renovation"]} filterTitle="Service Professionals (All)" />}
                    {active === "kyc" && <KYCReviewTable />}
                    {active === "properties" && <PropertiesTable />}
                    {active === "featured_requests" && <FeaturedRequestsTable />}
                    {active === "reels" && <AdminReels />}
                    {active === "gpm_partners" && <PartnersPanel />}
                    {active === "listing_credits" && <ListingCreditsTable />}
                    {active === "broadcasts" && <BroadcastsPanel />}
                    {active === "auctions" && <AuctionsAdmin />}
                    {active === "inquiries" && <InquiriesTable />}
                    {active === "support" && <SupportMessagesTable />}
                    {active === "reviews" && <ReviewsTable />}
                    {active === "withdrawals" && <WithdrawalsTable />}
                    {active === "settings" && <SettingsView />}
                </div>
            </div>
        </div>
    );
}

/* ---------------- OVERVIEW ---------------- */
function Overview() {
    const [stats, setStats] = useState({});
    const [analytics, setAnalytics] = useState({});

    useEffect(() => {
        api.get("/admin/stats").then((r) => setStats(r.data));
        api.get("/admin/analytics").then((r) => setAnalytics(r.data));
    }, []);

    return (
        <div className="space-y-8">
            <Header title="Platform Overview" subtitle="Real-time stats across the entire ecosystem." />

            <OverLimitReminderPanel />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats-grid">
                <Stat icon={Users} label="Total Users" value={stats.total_users} accent />
                <Stat icon={Building2} label="Properties" value={stats.total_properties} />
                <Stat icon={ShieldCheck} label="Verified Partners" value={stats.verified_partners} />
                <Stat icon={MessageSquare} label="Inquiries" value={stats.total_inquiries} />
                <Stat icon={BadgeCheck} label="Brokers" value={stats.total_brokers} />
                <Stat icon={Scale} label="Advocates" value={stats.total_advocates} />
                <Stat icon={BadgeIndianRupee} label="Loan Agents" value={stats.total_loan_agents} />
                <Stat icon={Star} label="Reviews" value={stats.total_reviews || 0} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Panel title="Listings vs Inquiries (last 6 months)">
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={analytics.timeline || []}>
                                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                                <XAxis dataKey="month" stroke="#A0AABF" fontSize={11} />
                                <YAxis stroke="#A0AABF" fontSize={11} />
                                <Tooltip contentStyle={{ background: "#0A1020", border: "1px solid #D4AF37" }} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Line type="monotone" dataKey="listings" stroke={GOLD} strokeWidth={2.5} dot={{ fill: GOLD, r: 4 }} />
                                <Line type="monotone" dataKey="inquiries" stroke="#F3E5AB" strokeWidth={2.5} strokeDasharray="4 4" dot={{ fill: "#F3E5AB", r: 4 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </Panel>

                <Panel title="Properties by Type">
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={analytics.properties_by_type || []} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}>
                                    {(analytics.properties_by_type || []).map((_, i) => (
                                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ background: "#0A1020", border: "1px solid #D4AF37" }} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </Panel>

                <Panel title="Users by Role">
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={analytics.users_by_role || []}>
                                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                                <XAxis dataKey="role" stroke="#A0AABF" fontSize={11} />
                                <YAxis stroke="#A0AABF" fontSize={11} />
                                <Tooltip contentStyle={{ background: "#0A1020", border: "1px solid #D4AF37" }} />
                                <Bar dataKey="count" fill={GOLD} radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Panel>

                <Panel title="Top Cities by Listings">
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={analytics.top_cities || []} layout="vertical">
                                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                                <XAxis type="number" stroke="#A0AABF" fontSize={11} />
                                <YAxis type="category" dataKey="city" stroke="#A0AABF" fontSize={11} width={80} />
                                <Tooltip contentStyle={{ background: "#0A1020", border: "1px solid #D4AF37" }} />
                                <Bar dataKey="count" fill={GOLD} radius={[0, 6, 6, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Panel>
            </div>
        </div>
    );
}

/** Admin control panel: preview + send bulk upgrade-reminder emails to over-limit users. */
function OverLimitReminderPanel() {
    const [preview, setPreview] = useState(null); // { eligible, sent, dry_run }
    const [sending, setSending] = useState(false);

    const runDry = () => {
        setSending(true);
        api.post("/admin/notify-over-limit-users?dry_run=true")
            .then((r) => setPreview(r.data))
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to preview"))
            .finally(() => setSending(false));
    };

    const sendNow = async (resend = false) => {
        if (!window.confirm(resend
            ? "Send upgrade-reminder emails to ALL over-limit users (including those already reminded)?"
            : "Send upgrade-reminder emails + in-app notifications to over-limit users?"
        )) return;
        setSending(true);
        try {
            const { data } = await api.post(`/admin/notify-over-limit-users${resend ? "?resend=true" : ""}`);
            toast.success(`Sent to ${data.sent} of ${data.eligible} eligible users`);
            setPreview(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="rounded-xl border border-gold-500/30 bg-navy-900/60 p-4 sm:p-5" data-testid="admin-overlimit-panel">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-gold-400 shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-white">Over-limit users — upgrade reminders</div>
                    <div className="text-xs text-white/60 mt-0.5">
                        Notify users who have more than 1 property but no active paid subscription. Sends
                        both email and an in-app notification. Idempotent — already-reminded users are
                        skipped unless you choose Re-send.
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={runDry}
                        disabled={sending}
                        data-testid="admin-overlimit-preview"
                        className="px-3 py-1.5 rounded-md border border-white/20 text-white text-xs font-semibold hover:bg-white/5 disabled:opacity-50"
                    >
                        Preview count
                    </button>
                    <button
                        type="button"
                        onClick={() => sendNow(false)}
                        disabled={sending}
                        data-testid="admin-overlimit-send"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gold-500 text-navy-900 text-xs font-extrabold uppercase tracking-wider hover:bg-gold-400 disabled:opacity-60"
                    >
                        <Send className="w-3.5 h-3.5" />
                        {sending ? "Sending…" : "Send reminders"}
                    </button>
                    <button
                        type="button"
                        onClick={() => sendNow(true)}
                        disabled={sending}
                        data-testid="admin-overlimit-resend"
                        className="px-3 py-1.5 rounded-md border border-gold-500/40 text-gold-400 text-xs font-semibold hover:bg-gold-500/10 disabled:opacity-50"
                    >
                        Re-send all
                    </button>
                </div>
            </div>
            {preview && (
                <div className="mt-3 text-xs text-white/70" data-testid="admin-overlimit-result">
                    {preview.dry_run
                        ? <>Eligible: <strong className="text-gold-400">{preview.eligible}</strong> user(s) will receive a reminder.</>
                        : <>Sent to <strong className="text-gold-400">{preview.sent}</strong> of {preview.eligible} eligible user(s).</>}
                </div>
            )}
        </div>
    );
}



/* ---------------- USERS ---------------- */
function UsersTable({ filter, filterTitle }) {
    const nav = useNavigate();
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState("");
    const [verifiedOnly, setVerifiedOnly] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [creating, setCreating] = useState(false);
    const [messagingId, setMessagingId] = useState(null); // shows a spinner on the clicked row
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [broadcastOpen, setBroadcastOpen] = useState(false);

    const reload = () => api.get("/admin/users").then((r) => setUsers(r.data || []));
    useEffect(() => { reload(); }, []);

    const messageUser = async (uid) => {
        setMessagingId(uid);
        try {
            const { data } = await api.post("/chat/conversations", { participant_id: uid });
            nav(`/chat/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not open chat with this user");
        } finally {
            setMessagingId(null);
        }
    };

    const toggleSelect = (uid) => setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(uid)) next.delete(uid); else next.add(uid);
        return next;
    });
    const clearSelection = () => setSelectedIds(new Set());
    const selectAllVisible = (checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            filtered.forEach((u) => { if (u.role !== "admin") { if (checked) next.add(u.id); else next.delete(u.id); } });
            return next;
        });
    };

    const verify = async (id, val) => {
        await api.put(`/admin/users/${id}/verify`, null, { params: { verified: val } });
        toast.success(val ? "Partner verified" : "Verification removed");
        reload();
    };
    const del = async (id) => {
        if (!window.confirm("Delete this user permanently?")) return;
        await api.delete(`/admin/users/${id}`);
        toast.success("User deleted");
        reload();
    };

    // `filter` accepts either a single role string or an array of roles. `null` = all users.
    const filterRoles = Array.isArray(filter) ? filter : (filter ? [filter] : null);
    const filtered = users
        .filter((u) => (filterRoles ? filterRoles.includes(u.role) : true))
        .filter((u) => (verifiedOnly ? u.verified : true))
        .filter((u) => {
            if (!search) return true;
            const s = search.toLowerCase();
            return u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s) || u.city?.toLowerCase().includes(s);
        });

    const title = filterTitle || (filterRoles && filterRoles.length === 1
        ? { broker: "Brokers", advocate: "Advocates", loan_agent: "Loan Agents", seller: "Sellers / Owners", builder: "Builders / Developers", architect: "Architects", interior_designer: "Interior Designers", civil_contractor: "Civil Contractors", civil_engineer: "Civil Engineers", property_valuer: "Property Valuers", renovation: "Renovation", user: "Buyers / Tenants", buyer: "Buyers / Tenants" }[filterRoles[0]] || "Users"
        : "All Users");

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <Header title={title} subtitle={`${filtered.length} record${filtered.length !== 1 ? "s" : ""}`} />
                <button
                    onClick={() => setCreating(true)}
                    data-testid="admin-add-user-btn"
                    className="px-4 py-2.5 rounded-md bg-gold-500 text-navy-900 font-semibold text-sm hover:bg-gold-100 flex items-center gap-2 self-start"
                >
                    <Plus className="w-4 h-4" /> Add {filter ? title.replace(/s$/, "") : "Partner"}
                </button>
            </div>

            <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name, email, city…"
                        data-testid="admin-users-search"
                        className="w-full bg-navy-900 border border-white/10 rounded-md pl-9 pr-3 py-2.5 text-sm text-white"
                    />
                </div>
                {filter && (
                    <label className="flex items-center gap-2 text-sm text-white/70 px-3 bg-navy-900 border border-white/10 rounded-md">
                        <input type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} className="accent-gold-500" />
                        Verified only
                    </label>
                )}
            </div>

            <div className="overflow-x-auto rounded-xl border border-white/5">
                <table className="w-full text-sm">
                    <thead className="bg-navy-950 text-xs uppercase tracking-wider text-white/50">
                        <tr>
                            <th className="text-left px-3 py-3 w-8">
                                <input
                                    type="checkbox"
                                    data-testid="admin-users-select-all"
                                    checked={filtered.filter((u) => u.role !== "admin").length > 0 && filtered.filter((u) => u.role !== "admin").every((u) => selectedIds.has(u.id))}
                                    onChange={(e) => selectAllVisible(e.target.checked)}
                                    className="accent-gold-500"
                                    title="Select all visible users"
                                />
                            </th>
                            <th className="text-left px-4 py-3">Name</th>
                            <th className="text-left px-4 py-3">Email</th>
                            <th className="text-left px-4 py-3">Role</th>
                            <th className="text-left px-4 py-3">City</th>
                            <th className="text-left px-4 py-3">Email ✓</th>
                            <th className="text-left px-4 py-3">Mobile ✓</th>
                            <th className="text-left px-4 py-3">Verified</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {filtered.map((u) => (
                            <tr key={u.id} className={`bg-navy-900 hover:bg-navy-800/60 ${selectedIds.has(u.id) ? "bg-gold-500/5" : ""}`} data-testid={`admin-user-${u.id}`}>
                                <td className="px-3 py-3">
                                    {u.role !== "admin" && (
                                        <input
                                            type="checkbox"
                                            data-testid={`admin-user-select-${u.id}`}
                                            checked={selectedIds.has(u.id)}
                                            onChange={() => toggleSelect(u.id)}
                                            className="accent-gold-500"
                                        />
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                        {u.avatar_url ? (
                                            <img src={u.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-gold-500/20 flex items-center justify-center text-gold-500 text-xs font-bold">
                                                {u.name?.[0]?.toUpperCase()}
                                            </div>
                                        )}
                                        <span className="font-semibold text-white">{u.name}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-white/70">{u.email}</td>
                                <td className="px-4 py-3">
                                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-gold-500/10 text-gold-500 border border-gold-500/30">{u.role.replace("_", " ")}</span>
                                </td>
                                <td className="px-4 py-3 text-white/70">{u.city || "—"}</td>
                                <td className="px-4 py-3">
                                    {u.email_verified
                                        ? <span className="text-emerald-400 text-xs inline-flex items-center gap-1" data-testid={`admin-email-verified-${u.id}`}><ShieldCheck className="w-3 h-3" /> Yes</span>
                                        : <span className="text-amber-400 text-xs" data-testid={`admin-email-unverified-${u.id}`}>Pending</span>}
                                </td>
                                <td className="px-4 py-3">
                                    {u.mobile_verified
                                        ? <span className="text-emerald-400 text-xs inline-flex items-center gap-1" data-testid={`admin-mobile-verified-${u.id}`}><ShieldCheck className="w-3 h-3" /> Yes</span>
                                        : <span className="text-white/40 text-xs" data-testid={`admin-mobile-unverified-${u.id}`}>—</span>}
                                </td>
                                <td className="px-4 py-3">
                                    {u.verified
                                        ? <span className="text-green-400 text-xs flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Verified</span>
                                        : <span className="text-white/50 text-xs">Pending</span>}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                        {u.role !== "admin" && (
                                            <button
                                                data-testid={`admin-edit-${u.id}`}
                                                onClick={() => setEditingUser(u)}
                                                className="text-xs px-3 py-1 rounded border border-white/15 text-white/80 hover:border-gold-500/40 hover:text-gold-500 flex items-center gap-1"
                                            ><Edit3 className="w-3 h-3" /> Edit</button>
                                        )}
                                        {u.role !== "admin" && u.role !== "user" && (
                                            <button
                                                data-testid={`admin-verify-${u.id}`}
                                                onClick={() => verify(u.id, !u.verified)}
                                                className="text-xs px-3 py-1 rounded border border-gold-500/40 text-gold-500 hover:bg-gold-500/10"
                                            >{u.verified ? "Unverify" : "Verify"}</button>
                                        )}
                                        {u.role !== "admin" && (
                                            <button
                                                data-testid={`admin-message-${u.id}`}
                                                onClick={() => messageUser(u.id)}
                                                disabled={messagingId === u.id}
                                                title={`Send a direct message to ${u.name || u.email}`}
                                                className="text-xs px-3 py-1 rounded border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-1 disabled:opacity-50"
                                            >
                                                {messagingId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
                                                Message
                                            </button>
                                        )}
                                        {u.role !== "admin" && (
                                            <button
                                                data-testid={`admin-delete-user-${u.id}`}
                                                onClick={() => del(u.id)}
                                                title={`Delete ${u.name || u.email}`}
                                                className="text-xs px-3 py-1 rounded border border-red-500/50 text-red-400 hover:bg-red-500/10 flex items-center gap-1"
                                            >
                                                <Trash2 className="w-3 h-3" /> Delete
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr><td colSpan="8" className="text-center py-12 text-white/50 bg-navy-900">No matching records</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <UserFormModal
                open={creating}
                onClose={() => setCreating(false)}
                user={null}
                onSaved={reload}
            />
            <UserFormModal
                open={!!editingUser}
                onClose={() => setEditingUser(null)}
                user={editingUser}
                onSaved={reload}
            />

            {/* Floating bulk-action bar — appears when the admin has selected 1+ users */}
            {selectedIds.size > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 px-4 w-full max-w-2xl" data-testid="admin-bulk-actionbar">
                    <div className="bg-navy-900 border border-gold-500/40 shadow-2xl rounded-full pl-5 pr-3 py-2 flex items-center gap-3">
                        <span className="text-sm text-white/85 font-semibold whitespace-nowrap">
                            <strong className="text-gold-400">{selectedIds.size}</strong> selected
                        </span>
                        <button
                            onClick={() => setBroadcastOpen(true)}
                            data-testid="admin-bulk-broadcast-btn"
                            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-500 text-navy-900 font-extrabold text-sm hover:bg-gold-400"
                        >
                            <Send className="w-4 h-4" /> Broadcast Message
                        </button>
                        <button
                            onClick={clearSelection}
                            data-testid="admin-bulk-clear-btn"
                            title="Clear selection"
                            className="p-2 rounded-full text-white/60 hover:text-white hover:bg-white/10"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            <BroadcastModal
                open={broadcastOpen}
                onClose={() => setBroadcastOpen(false)}
                userIds={Array.from(selectedIds)}
                onSent={() => { setBroadcastOpen(false); clearSelection(); }}
            />
        </div>
    );
}

/* ---------------- BULK BROADCAST MODAL ---------------- */
function BroadcastModal({ open, onClose, userIds, onSent }) {
    const [text, setText] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [sending, setSending] = useState(false);
    useEffect(() => { if (open) { setText(""); setImageUrl(""); } }, [open]);
    if (!open) return null;

    const send = async () => {
        const body = text.trim();
        if (!body && !imageUrl.trim()) {
            toast.error("Add a message or an image URL first");
            return;
        }
        if (userIds.length === 0) {
            toast.error("No users selected");
            return;
        }
        setSending(true);
        try {
            const { data } = await api.post("/admin/chat/broadcast", {
                user_ids: userIds,
                text: body || null,
                image_url: imageUrl.trim() || null,
            });
            toast.success(`Broadcast sent to ${data.sent} of ${data.total} user${data.total !== 1 ? "s" : ""}${data.failed ? ` (${data.failed} failed)` : ""}`);
            onSent?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Broadcast failed");
        } finally {
            setSending(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="admin-broadcast-modal">
            <div className="bg-navy-900 border border-gold-500/30 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-gold-400 font-extrabold">Bulk Broadcast</div>
                        <h3 className="font-heading text-lg font-bold text-white">Message {userIds.length} user{userIds.length !== 1 ? "s" : ""}</h3>
                    </div>
                    <button onClick={onClose} className="text-white/50 hover:text-white p-1"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold block mb-1.5">Announcement message</label>
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={5}
                            maxLength={4000}
                            data-testid="admin-broadcast-text"
                            placeholder="Type your announcement… (arrives in each recipient's chat with the Admin/Support thread)"
                            className="w-full bg-navy-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold-500/50 resize-none"
                        />
                        <div className="text-[10px] text-white/40 mt-1 text-right">{text.length}/4000</div>
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold block mb-1.5">Optional image URL</label>
                        <input
                            value={imageUrl}
                            onChange={(e) => setImageUrl(e.target.value)}
                            placeholder="https://…"
                            data-testid="admin-broadcast-image"
                            className="w-full bg-navy-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold-500/50"
                        />
                    </div>
                    <div className="rounded-lg bg-gold-500/5 border border-gold-500/20 px-3 py-2 text-[12px] text-gold-200">
                        <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                        Each recipient will see this in their own 1-on-1 admin chat. They can reply — replies land in that user&apos;s thread only, not to everyone.
                    </div>
                </div>
                <div className="px-5 py-4 border-t border-white/10 flex items-center justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 rounded-md text-sm text-white/70 hover:text-white">Cancel</button>
                    <button
                        onClick={send}
                        disabled={sending}
                        data-testid="admin-broadcast-send"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gold-500 text-navy-900 font-extrabold text-sm hover:bg-gold-400 disabled:opacity-50"
                    >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {sending ? "Sending…" : `Send to ${userIds.length}`}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}

/* ---------------- PROPERTIES ---------------- */
function PropertiesTable() {
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [visibilityFilter, setVisibilityFilter] = useState("all"); // all | visible | hidden
    const [owners, setOwners] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const [creatingAuction, setCreatingAuction] = useState(false);
    const reload = () => api.get("/admin/properties").then((r) => setItems(r.data || []));
    useEffect(() => {
        reload();
        api.get("/admin/users").then((r) => setOwners((r.data || []).filter((u) => u.role !== "user")));
    }, []);

    const toggleFeatured = async (p) => {
        await api.put(`/admin/properties/${p.id}/feature`, null, { params: { featured: !p.featured } });
        toast.success(!p.featured ? "Featured" : "Unfeatured");
        reload();
    };
    const toggleVisibility = async (p) => {
        const willBeVisible = p.status !== "active";
        await api.put(`/admin/properties/${p.id}/visibility`, null, { params: { visible: willBeVisible } });
        toast.success(willBeVisible ? "Property is now visible on site" : "Property hidden from public");
        reload();
    };
    const del = async (id) => {
        if (!window.confirm("Delete this property permanently?")) return;
        await api.delete(`/admin/properties/${id}`);
        toast.success("Property deleted");
        reload();
    };

    const filtered = items.filter((p) => {
        if (visibilityFilter === "visible" && p.status !== "active") return false;
        if (visibilityFilter === "hidden" && p.status === "active") return false;
        if (!search) return true;
        const s = search.toLowerCase();
        return p.title?.toLowerCase().includes(s) || p.city?.toLowerCase().includes(s) || p.owner_name?.toLowerCase().includes(s);
    });

    const visibleCount = items.filter((p) => p.status === "active").length;
    const hiddenCount = items.length - visibleCount;

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <Header title="Properties" subtitle={`${filtered.length} shown · ${visibleCount} visible · ${hiddenCount} hidden`} />
                <button
                    onClick={() => setCreating(true)}
                    data-testid="admin-add-property-btn"
                    className="px-4 py-2.5 rounded-md bg-gold-500 text-navy-900 font-semibold text-sm hover:bg-gold-100 flex items-center gap-2 self-start"
                >
                    <Plus className="w-4 h-4" /> Add Property
                </button>
            </div>

            <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search title, city, owner…"
                        data-testid="admin-properties-search"
                        className="w-full bg-navy-900 border border-white/10 rounded-md pl-9 pr-3 py-2.5 text-sm text-white"
                    />
                </div>
                <div className="flex gap-2" data-testid="admin-properties-visibility-filter">
                    {[
                        { v: "all", label: `All (${items.length})` },
                        { v: "visible", label: `Visible (${visibleCount})` },
                        { v: "hidden", label: `Hidden (${hiddenCount})` },
                    ].map((o) => (
                        <button
                            key={o.v}
                            onClick={() => setVisibilityFilter(o.v)}
                            data-testid={`admin-visibility-${o.v}`}
                            className={`px-3 py-2 rounded-md text-xs font-semibold whitespace-nowrap ${
                                visibilityFilter === o.v
                                    ? "bg-gold-500 text-navy-900"
                                    : "border border-white/10 text-white/70 hover:border-gold-500/40"
                            }`}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filtered.map((p) => (
                    <div key={p.id} className={`rounded-xl bg-navy-900 border p-4 flex gap-4 ${p.status === "active" ? "border-white/5 hover:border-gold-500/40" : "border-red-500/30 opacity-80"}`} data-testid={`admin-property-${p.id}`}>
                        <div className="w-32 aspect-[4/3] rounded-md overflow-hidden bg-navy-800 shrink-0 relative">
                            {p.images?.[0] && <img src={fileUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                            {p.status !== "active" && (
                                <div className="absolute inset-0 bg-navy-950/70 flex items-center justify-center">
                                    <span className="text-[10px] uppercase tracking-widest text-red-300 font-bold">Hidden</span>
                                </div>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                                <Link to={`/properties/${p.id}`} target="_blank" className="font-heading font-bold text-white hover:text-gold-500 truncate block">{p.title}</Link>
                                <div className="flex gap-1 shrink-0">
                                    {p.featured && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-gold-500 text-navy-900 font-bold">Featured</span>}
                                    <span
                                        data-testid={`admin-status-pill-${p.id}`}
                                        className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${
                                            p.status === "active"
                                                ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                                                : "bg-red-500/15 text-red-300 border border-red-500/30"
                                        }`}
                                    >
                                        {p.status === "active" ? "Visible" : "Hidden"}
                                    </span>
                                </div>
                            </div>
                            <div className="text-xs text-white/55 mt-1">{p.city} · {p.property_type} · For {p.listing_type}</div>
                            <div className="text-gold-500 font-bold mt-1">{formatINR(p.price)}</div>
                            <div className="text-xs text-white/50 mt-1">By {p.owner_name}</div>
                            <div className="flex flex-wrap gap-2 mt-3">
                                <button
                                    onClick={() => toggleVisibility(p)}
                                    data-testid={`admin-visibility-toggle-${p.id}`}
                                    className={`text-xs px-3 py-1.5 rounded border flex items-center gap-1 ${
                                        p.status === "active"
                                            ? "border-red-500/40 text-red-400 hover:bg-red-500/10"
                                            : "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                                    }`}
                                >
                                    {p.status === "active" ? "Hide" : "Show"}
                                </button>
                                <button
                                    onClick={() => setEditing(p)}
                                    data-testid={`admin-edit-property-${p.id}`}
                                    className="text-xs px-3 py-1.5 rounded border border-white/15 text-white/80 hover:border-gold-500/40 hover:text-gold-500 flex items-center gap-1"
                                ><Edit3 className="w-3 h-3" /> Edit</button>
                                <button
                                    onClick={() => toggleFeatured(p)}
                                    data-testid={`admin-feature-${p.id}`}
                                    className="text-xs px-3 py-1.5 rounded border border-gold-500/40 text-gold-500 hover:bg-gold-500/10"
                                >{p.featured ? "Unfeature" : "Feature"}</button>
                                <button
                                    onClick={() => del(p.id)}
                                    data-testid={`admin-delete-property-${p.id}`}
                                    className="text-xs px-3 py-1.5 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10"
                                >Delete</button>
                            </div>
                        </div>
                    </div>
                ))}
                {filtered.length === 0 && <div className="lg:col-span-2 text-center py-12 text-white/50 rounded-xl bg-navy-900 border border-white/5">No properties</div>}
            </div>

            <PropertyFormModal
                open={creating}
                onClose={() => setCreating(false)}
                property={null}
                owners={owners}
                onSaved={reload}
                onSwitchToAuction={() => setCreatingAuction(true)}
            />
            <PropertyFormModal
                open={!!editing}
                onClose={() => setEditing(null)}
                property={editing}
                owners={owners}
                onSaved={reload}
            />
            <AuctionFormModal
                open={creatingAuction}
                onClose={() => setCreatingAuction(false)}
                auction={null}
                onSaved={reload}
            />
        </div>
    );
}

/* ---------------- INQUIRIES ---------------- */
function InquiriesTable() {
    const [items, setItems] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");

    const reload = () => api.get("/inquiries").then((r) => setItems(r.data || []));
    useEffect(() => { reload(); }, []);

    const updateStatus = async (id, status) => {
        await api.put(`/admin/inquiries/${id}/status`, null, { params: { status } });
        toast.success(`Marked as ${status}`);
        reload();
    };
    const del = async (id) => {
        if (!window.confirm("Delete inquiry?")) return;
        await api.delete(`/admin/inquiries/${id}`);
        toast.success("Deleted");
        reload();
    };

    const filtered = statusFilter ? items.filter((i) => i.status === statusFilter) : items;

    return (
        <div className="space-y-6">
            <Header title="Inquiries / Leads" subtitle={`${filtered.length} total — track and update lead status.`} />

            <div className="flex gap-2">
                {[{ v: "", l: "All" }, { v: "new", l: "New" }, { v: "contacted", l: "Contacted" }, { v: "closed", l: "Closed" }].map((o) => (
                    <button
                        key={o.v}
                        onClick={() => setStatusFilter(o.v)}
                        data-testid={`admin-inquiry-filter-${o.v || "all"}`}
                        className={`px-4 py-2 rounded-md text-xs font-semibold ${
                            statusFilter === o.v ? "bg-gold-500 text-navy-900" : "border border-white/10 text-white/70 hover:border-gold-500/40"
                        }`}
                    >{o.l}</button>
                ))}
            </div>

            <div className="space-y-3">
                {filtered.map((i) => (
                    <div key={i.id} className="rounded-xl bg-navy-900 border border-white/5 p-5" data-testid={`admin-inquiry-${i.id}`}>
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-heading font-bold text-white">{i.name}</span>
                                    <span className="text-xs text-white/50">·</span>
                                    <a href={`tel:${i.phone}`} className="text-xs text-gold-500 hover:underline">{i.phone}</a>
                                    <span className="text-xs text-white/50">·</span>
                                    <a href={`mailto:${i.email}`} className="text-xs text-gold-500 hover:underline">{i.email}</a>
                                </div>
                                <div className="text-xs text-white/55 mt-1">
                                    For: <Link to={`/properties/${i.property_id}`} target="_blank" className="text-white hover:text-gold-500">"{i.property_title}"</Link>
                                </div>
                                {i.message && <p className="text-sm text-white/70 mt-2 italic">"{i.message}"</p>}
                                <div className="text-[11px] text-white/40 mt-2">{new Date(i.created_at).toLocaleString()}</div>
                            </div>
                            <div className="flex flex-col md:items-end gap-2 shrink-0">
                                <select
                                    value={i.status}
                                    onChange={(e) => updateStatus(i.id, e.target.value)}
                                    data-testid={`admin-inquiry-status-${i.id}`}
                                    className="bg-navy-800 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white"
                                >
                                    <option value="new">New</option>
                                    <option value="contacted">Contacted</option>
                                    <option value="closed">Closed</option>
                                </select>
                                <button onClick={() => del(i.id)} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1">
                                    <Trash2 className="w-3 h-3" /> Delete
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {filtered.length === 0 && <div className="text-center py-12 text-white/50 rounded-xl bg-navy-900 border border-white/5">No inquiries</div>}
            </div>
        </div>
    );
}

/* ---------------- SUPPORT MESSAGES ---------------- */
function SupportMessagesTable() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("open"); // "open" | "resolved" | "all"
    const reload = () => {
        const params = filter === "all" ? {} : { resolved: filter === "resolved" };
        api.get("/admin/support-messages", { params }).then((r) => setItems(r.data?.items || []));
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

    const toggle = async (m) => {
        try {
            await api.put(`/admin/support-messages/${m.id}/resolve`, null, { params: { resolved: !m.resolved } });
            toast.success(!m.resolved ? "Marked resolved" : "Reopened");
            reload();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div>
            <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
                <div>
                    <h2 className="font-heading text-2xl font-bold text-white">Support Messages</h2>
                    <p className="text-white/60 text-sm mt-0.5">Public support requests submitted via /contact</p>
                </div>
                <div className="inline-flex rounded-md border border-white/10 p-1 bg-navy-900" data-testid="admin-support-filter">
                    {["open", "resolved", "all"].map((f) => (
                        <button
                            key={f}
                            type="button"
                            onClick={() => setFilter(f)}
                            data-testid={`admin-support-filter-${f}`}
                            className={`px-3 py-1.5 text-xs font-semibold rounded ${filter === f ? "bg-gold-500 text-navy-900" : "text-white/70 hover:text-white"}`}
                        >{f.charAt(0).toUpperCase() + f.slice(1)}</button>
                    ))}
                </div>
            </div>

            {items.length === 0 ? (
                <div className="rounded-2xl bg-navy-800 border border-white/5 p-10 text-center text-white/60 text-sm">
                    No support messages{filter !== "all" ? ` in "${filter}" filter` : ""} yet.
                </div>
            ) : (
                <div className="space-y-3" data-testid="admin-support-list">
                    {items.map((m) => (
                        <div key={m.id} data-testid={`admin-support-${m.id}`} className="rounded-2xl bg-navy-800 border border-white/5 p-4 sm:p-5 hover:border-gold-500/30">
                            <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                                <div>
                                    <div className="text-sm font-bold text-white">{m.name}</div>
                                    <a href={`mailto:${m.email}`} className="text-xs text-gold-400 hover:text-gold-200">{m.email}</a>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] uppercase tracking-widest text-white/50">
                                        {new Date(m.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                                    </span>
                                    <button
                                        onClick={() => toggle(m)}
                                        data-testid={`admin-support-toggle-${m.id}`}
                                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border ${
                                            m.resolved
                                                ? "border-white/15 text-white/60 hover:text-white"
                                                : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                                        }`}
                                    >
                                        {m.resolved ? "Reopen" : (<><Check className="w-3 h-3" /> Resolve</>)}
                                    </button>
                                </div>
                            </div>
                            <p className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed">{m.message}</p>
                            <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${m.resolved ? "bg-white/5 text-white/50" : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"}`}>
                                    {m.resolved ? "Resolved" : "Open"}
                                </span>
                                <a
                                    href={`mailto:${m.email}?subject=Re%3A%20Your%20Global%20Property%20Mart%20support%20request&body=${encodeURIComponent(`Hi ${m.name},\n\nThanks for reaching out. Regarding your message:\n\n> ${m.message}\n\n`)}`}
                                    data-testid={`admin-support-reply-${m.id}`}
                                    className="text-xs font-bold text-gold-400 hover:text-gold-200"
                                >
                                    Reply via email →
                                </a>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ---------------- REVIEWS ---------------- */
function ReviewsTable() {
    const [items, setItems] = useState([]);
    const [reports, setReports] = useState([]);
    const [tab, setTab] = useState("all"); // "all" | "reported"
    const reload = async () => {
        const [all, rep] = await Promise.all([
            api.get("/admin/reviews"),
            api.get("/admin/reviews/reports"),
        ]);
        setItems(all.data || []);
        setReports(rep.data || []);
    };
    useEffect(() => { reload(); }, []);

    const del = async (id) => {
        if (!window.confirm("Delete review? Target's rating will recompute.")) return;
        await api.delete(`/admin/reviews/${id}`);
        toast.success("Review removed");
        reload();
    };

    const hide = async (id) => {
        await api.post(`/admin/reviews/${id}/hide`);
        toast.success("Review hidden from public");
        reload();
    };

    const unhide = async (id) => {
        await api.post(`/admin/reviews/${id}/unhide`);
        toast.success("Review restored");
        reload();
    };

    const list = tab === "reported" ? reports : items;
    const reportedCount = reports.length;

    return (
        <div className="space-y-6">
            <Header title="Reviews Moderation" subtitle="Hide, restore or delete inappropriate & fake reviews. Verified reviews come from real enquiry interactions." />
            <div className="flex gap-2">
                <button
                    onClick={() => setTab("all")}
                    data-testid="admin-reviews-tab-all"
                    className={`px-3 py-1.5 rounded-full text-xs font-bold ${tab === "all" ? "bg-gold-500 text-navy-500" : "bg-white/5 text-white/70 border border-white/15 hover:border-gold-500/40"}`}
                >
                    All reviews ({items.length})
                </button>
                <button
                    onClick={() => setTab("reported")}
                    data-testid="admin-reviews-tab-reported"
                    className={`px-3 py-1.5 rounded-full text-xs font-bold ${tab === "reported" ? "bg-red-500 text-white" : "bg-white/5 text-white/70 border border-white/15 hover:border-red-400/50"}`}
                >
                    Reported ({reportedCount})
                </button>
            </div>
            <div className="space-y-3">
                {list.map((r) => (
                    <div key={r.id} className="rounded-xl bg-navy-900 border border-white/5 p-5" data-testid={`admin-review-${r.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-white">{r.user_name}</span>
                                    <span className="flex items-center gap-0.5 text-gold-500 text-sm">
                                        {[1,2,3,4,5].map((s) => <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? "fill-gold-500" : "opacity-30"}`} />)}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-wider text-white/50">on {r.target_type}</span>
                                    {r.verified && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-bold">Verified</span>}
                                    {r.hidden && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-white/10 border border-white/20 text-white/70 font-bold">Hidden</span>}
                                    {(r.reports_count || 0) > 0 && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-red-500/20 border border-red-500/40 text-red-300 font-bold">{r.reports_count} report{r.reports_count !== 1 ? "s" : ""}</span>}
                                </div>
                                {r.comment && <p className="text-sm text-white/75 mt-2">&quot;{r.comment}&quot;</p>}
                                <div className="text-[11px] text-white/40 mt-2">{new Date(r.created_at).toLocaleString()}</div>
                                {tab === "reported" && r.reports?.length > 0 && (
                                    <div className="mt-2 rounded-lg bg-red-500/5 border border-red-500/20 p-2 space-y-1">
                                        {r.reports.map((rp) => (
                                            <div key={rp.id} className="text-[11px] text-white/70">
                                                <strong>{rp.reporter_name || "Someone"}</strong>: {rp.reason || <em className="text-white/40">(no reason given)</em>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                {r.hidden ? (
                                    <button onClick={() => unhide(r.id)} title="Restore visibility" data-testid={`admin-review-unhide-${r.id}`} className="text-emerald-400 hover:text-emerald-300 p-1.5 rounded-md hover:bg-emerald-500/10"><Eye className="w-4 h-4" /></button>
                                ) : (
                                    <button onClick={() => hide(r.id)} title="Hide from public" data-testid={`admin-review-hide-${r.id}`} className="text-amber-400 hover:text-amber-300 p-1.5 rounded-md hover:bg-amber-500/10"><EyeOff className="w-4 h-4" /></button>
                                )}
                                <button onClick={() => del(r.id)} title="Delete permanently" data-testid={`admin-review-delete-${r.id}`} className="text-red-400 hover:text-red-300 p-1.5 rounded-md hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                            </div>
                        </div>
                    </div>
                ))}
                {list.length === 0 && <div className="text-center py-12 text-white/50 rounded-xl bg-navy-900 border border-white/5">{tab === "reported" ? "No reported reviews — nice and clean." : "No reviews yet"}</div>}
            </div>
        </div>
    );
}


/* ---------------- LISTING CREDITS (Pay-Per-Listing ₹99) ---------------- */
function ListingCreditsTable() {
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("all"); // all | pending | available | used
    const reload = () =>
        api.get("/admin/listing-credits", { params: filter === "all" ? {} : { status: filter } })
            .then((r) => setData(r.data))
            .catch(() => setData({ items: [], counts: {}, revenue_inr: 0, total: 0 }));
    useEffect(() => { reload(); }, [filter]);

    if (!data) return <div className="text-white/50 py-8 text-center">Loading…</div>;
    const items = data.items || [];
    const counts = data.counts || {};

    const badge = (status) => {
        const map = {
            pending:   "bg-white/10 text-white/70 border-white/20",
            available: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
            used:      "bg-gold-500/20 text-gold-300 border-gold-500/40",
        };
        return <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border font-extrabold ${map[status] || "bg-white/5 text-white/50 border-white/10"}`}>{status}</span>;
    };

    return (
        <div className="space-y-6">
            <Header
                title="Listing Credits (Pay-Per-Listing)"
                subtitle="₹99 one-off purchases that unlock exactly ONE additional property listing each. Subscription plans (unlimited) are unaffected."
            />

            {/* Revenue + counts */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Revenue (₹)" value={`₹${(data.revenue_inr || 0).toLocaleString("en-IN")}`} accent />
                <StatCard label="Total Txns" value={data.total || 0} />
                <StatCard label="Available" value={counts.available || 0} />
                <StatCard label="Used" value={counts.used || 0} />
            </div>

            {/* Filter chips */}
            <div className="flex gap-2 flex-wrap">
                {["all", "available", "used", "pending"].map((f) => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        data-testid={`admin-credits-filter-${f}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize ${filter === f ? "bg-gold-500 text-navy-900" : "bg-white/5 text-white/70 border border-white/15 hover:border-gold-500/40"}`}
                    >
                        {f}
                    </button>
                ))}
            </div>

            {/* Table */}
            <div className="rounded-xl bg-navy-900 border border-white/5 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-navy-950 text-xs uppercase tracking-wider text-white/50">
                        <tr>
                            <th className="text-left px-4 py-3">User</th>
                            <th className="text-left px-4 py-3">Amount</th>
                            <th className="text-left px-4 py-3">Order ID</th>
                            <th className="text-left px-4 py-3">Status</th>
                            <th className="text-left px-4 py-3">Purchased</th>
                            <th className="text-left px-4 py-3">Used on Property</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {items.map((c) => (
                            <tr key={c.id} className="hover:bg-navy-800/60" data-testid={`admin-credit-row-${c.id}`}>
                                <td className="px-4 py-3">
                                    <div className="font-semibold text-white">{c.user_name || "—"}</div>
                                    <div className="text-[11px] text-white/50">{c.user_email}</div>
                                </td>
                                <td className="px-4 py-3 text-gold-400 font-extrabold">₹{c.amount_inr}</td>
                                <td className="px-4 py-3 text-white/60 text-[11px] font-mono">{c.razorpay_order_id || "—"}</td>
                                <td className="px-4 py-3">{badge(c.status)}</td>
                                <td className="px-4 py-3 text-white/70 text-[12px]">{c.created_at ? new Date(c.created_at).toLocaleString() : "—"}</td>
                                <td className="px-4 py-3">
                                    {c.used_by_property_id
                                        ? <Link to={`/property/${c.used_by_property_id}`} className="text-emerald-400 hover:underline text-[12px] font-mono">{c.used_by_property_id.slice(0, 8)}…</Link>
                                        : <span className="text-white/30 text-[11px]">—</span>}
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan="6" className="text-center py-12 text-white/50">No transactions yet</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function StatCard({ label, value, accent }) {
    return (
        <div className={`rounded-xl p-4 border ${accent ? "bg-gold-500/10 border-gold-500/30" : "bg-navy-900 border-white/5"}`}>
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">{label}</div>
            <div className={`text-2xl font-heading font-bold mt-1 ${accent ? "text-gold-400" : "text-white"}`}>{value}</div>
        </div>
    );
}


/* ---------------- BROADCASTS PANEL (Send to All + History) ---------------- */
function BroadcastsPanel() {
    const [items, setItems] = useState(null);
    const [composeOpen, setComposeOpen] = useState(false);
    const reload = () =>
        api.get("/admin/chat/broadcasts")
            .then((r) => setItems(r.data?.items || []))
            .catch(() => setItems([]));
    useEffect(() => { reload(); }, []);

    if (!items) return <div className="text-white/50 py-8 text-center">Loading…</div>;

    return (
        <div className="space-y-6">
            <Header
                title="Broadcasts"
                subtitle="Send a single message to every registered user at once. Full delivery log below."
            />

            <div className="flex flex-wrap gap-3">
                <button
                    onClick={() => setComposeOpen(true)}
                    data-testid="admin-broadcast-all-cta"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold-500 text-navy-900 font-extrabold text-sm hover:bg-gold-400"
                >
                    <Send className="w-4 h-4" /> Send to All Users
                </button>
            </div>

            <div className="rounded-xl bg-navy-900 border border-white/5 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-navy-950 text-xs uppercase tracking-wider text-white/50">
                        <tr>
                            <th className="text-left px-4 py-3">Message</th>
                            <th className="text-left px-4 py-3">Sent</th>
                            <th className="text-left px-4 py-3">Failed</th>
                            <th className="text-left px-4 py-3">Target</th>
                            <th className="text-left px-4 py-3">When</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {items.map((b) => (
                            <tr key={b.id} className="hover:bg-navy-800/60" data-testid={`admin-broadcast-row-${b.id}`}>
                                <td className="px-4 py-3 max-w-md">
                                    <div className="text-white truncate">{b.text || <em className="text-white/40">[Image]</em>}</div>
                                    {b.forwarded_from && <div className="text-[10px] text-white/40 mt-0.5">↻ Forwarded</div>}
                                </td>
                                <td className="px-4 py-3 text-emerald-400 font-extrabold">{b.sent}</td>
                                <td className="px-4 py-3 text-red-400 font-extrabold">{b.failed || 0}</td>
                                <td className="px-4 py-3 text-white/70">{b.target_count}</td>
                                <td className="px-4 py-3 text-white/60 text-[12px]">{new Date(b.created_at).toLocaleString()}</td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan="5" className="text-center py-12 text-white/50">No broadcasts sent yet</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <BroadcastToAllModal
                open={composeOpen}
                onClose={() => setComposeOpen(false)}
                onSent={() => { setComposeOpen(false); reload(); }}
            />
        </div>
    );
}

function BroadcastToAllModal({ open, onClose, onSent }) {
    const [text, setText] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [sending, setSending] = useState(false);
    useEffect(() => { if (open) { setText(""); setImageUrl(""); } }, [open]);
    if (!open) return null;

    const send = async () => {
        if (!text.trim() && !imageUrl.trim()) { toast.error("Add a message or an image URL"); return; }
        if (!window.confirm("Send this message to ALL registered users?")) return;
        setSending(true);
        try {
            const { data } = await api.post("/admin/chat/broadcast", {
                all_users: true,
                text: text.trim() || null,
                image_url: imageUrl.trim() || null,
            });
            toast.success(`Broadcast delivered to ${data.sent} of ${data.total} user${data.total !== 1 ? "s" : ""}${data.failed ? ` (${data.failed} failed)` : ""}`);
            onSent?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Broadcast failed");
        } finally { setSending(false); }
    };

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="admin-broadcast-all-modal">
            <div className="bg-navy-900 border border-gold-500/30 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-gold-400 font-extrabold">Send to Everyone</div>
                        <h3 className="font-heading text-lg font-bold text-white">Broadcast to all users</h3>
                    </div>
                    <button onClick={onClose} className="text-white/50 hover:text-white p-1"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        rows={5}
                        maxLength={4000}
                        data-testid="admin-broadcast-all-text"
                        placeholder="Type your announcement…"
                        className="w-full bg-navy-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold-500/50 resize-none"
                    />
                    <input
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        placeholder="Optional image URL"
                        data-testid="admin-broadcast-all-image"
                        className="w-full bg-navy-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold-500/50"
                    />
                    <div className="rounded-lg bg-gold-500/5 border border-gold-500/20 px-3 py-2 text-[12px] text-gold-200">
                        <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                        Lands in every non-admin user&apos;s Admin/Support chat as a normal message with unread badge + notification bell alert.
                    </div>
                </div>
                <div className="px-5 py-4 border-t border-white/10 flex items-center justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 rounded-md text-sm text-white/70 hover:text-white">Cancel</button>
                    <button
                        onClick={send}
                        disabled={sending}
                        data-testid="admin-broadcast-all-send"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gold-500 text-navy-900 font-extrabold text-sm hover:bg-gold-400 disabled:opacity-50"
                    >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {sending ? "Sending…" : "Send to All"}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}



/* ---------------- FEATURED PROPERTY REQUESTS ---------------- */
function FeaturedRequestsTable() {
    const [items, setItems] = useState(null);
    const [tab, setTab] = useState("pending");
    const [busy, setBusy] = useState(null);

    const load = async () => {
        try {
            const r = await api.get("/admin/feature-requests", { params: { status: tab === "all" ? undefined : tab } });
            setItems(r.data || []);
        } catch {
            setItems([]);
        }
    };

    useEffect(() => { load(); }, [tab]);

    const approve = async (id) => {
        if (!window.confirm("Approve this feature request? The property will become Featured for 30 days.")) return;
        setBusy(id);
        try {
            await api.post(`/admin/feature-requests/${id}/approve`);
            toast.success("Feature approved — property is now ⭐ Featured");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to approve");
        } finally { setBusy(null); }
    };

    const reject = async (id) => {
        const reason = window.prompt("Reason for rejection (visible to user):", "Payment could not be verified. Please share bank statement proof.");
        if (reason === null) return;
        setBusy(id);
        try {
            await api.post(`/admin/feature-requests/${id}/reject`, { reason });
            toast.success("Request rejected");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to reject");
        } finally { setBusy(null); }
    };

    return (
        <div className="space-y-6">
            <Header title="Featured Property Requests" subtitle="Verify payment references and approve featured status manually." />
            <div className="flex gap-2 flex-wrap">
                {[
                    { v: "pending", l: "Pending" },
                    { v: "approved_active", l: "Active" },
                    { v: "rejected", l: "Rejected" },
                    { v: "expired", l: "Expired" },
                    { v: "all", l: "All" },
                ].map((t) => (
                    <button
                        key={t.v}
                        onClick={() => setTab(t.v)}
                        data-testid={`featured-req-tab-${t.v}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold ${tab === t.v ? "bg-gold-500 text-navy-500" : "bg-white text-navy-500/70 border border-navy-500/20 hover:border-gold-500/40"}`}
                    >
                        {t.l}
                    </button>
                ))}
            </div>

            {items === null && <div className="text-center py-10 text-navy-500/50">Loading…</div>}
            {items && items.length === 0 && (
                <div className="text-center py-10 text-navy-500/50 rounded-xl bg-white border border-navy-500/10">No requests in this tab.</div>
            )}
            {items && items.length > 0 && (
                <div className="space-y-3">
                    {items.map((r) => (
                        <div key={r.id} data-testid={`featured-req-item-${r.id}`} className="rounded-xl bg-white border border-navy-500/10 p-4 shadow-sm">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <a href={`/properties/${r.property_id}`} target="_blank" rel="noreferrer" className="font-heading font-bold text-navy-500 text-sm truncate hover:text-gold-700">
                                            {r.property_title}
                                        </a>
                                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${
                                            r.status === "pending" ? "bg-amber-100 text-amber-800 border border-amber-300" :
                                            r.status === "approved_active" ? "bg-emerald-100 text-emerald-800 border border-emerald-300" :
                                            r.status === "rejected" ? "bg-red-100 text-red-800 border border-red-300" :
                                            r.status === "awaiting_payment" ? "bg-navy-500/5 text-navy-500/60 border border-navy-500/15" :
                                            "bg-navy-500/5 text-navy-500/60 border border-navy-500/15"
                                        }`}>{r.status === "awaiting_payment" ? "Awaiting payment" : r.status.replace(/_/g, " ")}</span>
                                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-extrabold ${
                                            r.payment_status === "PAID" ? "bg-emerald-100 text-emerald-700 border border-emerald-400" :
                                            r.payment_status === "MANUAL_PENDING" ? "bg-amber-100 text-amber-800 border border-amber-400" :
                                            "bg-navy-500/5 text-navy-500/50 border border-navy-500/15"
                                        }`}>{r.payment_status === "PAID" ? "💳 PAID" : r.payment_status === "MANUAL_PENDING" ? "🧾 Manual UTR" : r.payment_status === "PENDING_PAYMENT" ? "⏳ Awaiting payment" : (r.payment_status || "—")}</span>
                                    </div>
                                    <div className="text-[11px] text-navy-500/65 mt-1">
                                        By <span className="text-navy-500 font-semibold">{r.user_name || r.user_email}</span> · Requested on {new Date(r.created_at).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                                    </div>
                                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                                        <StatSmall label="Amount" value={`₹${r.amount}`} />
                                        <StatSmall label="Method" value={(r.payment_method || "").toUpperCase() || "—"} />
                                        <StatSmall label={r.payment_method === "razorpay" ? "Razorpay Payment ID" : "UTR / Ref"} value={r.payment_reference || r.razorpay_payment_id || "—"} testid={`featured-req-ref-${r.id}`} />
                                        <StatSmall label="Property ID" value={r.property_id.slice(0, 8) + "…"} />
                                    </div>
                                    {r.note && <div className="mt-2 text-[12px] text-navy-500/75 italic">Note: {r.note}</div>}
                                    {r.status === "approved_active" && r.expires_at && (
                                        <div className="mt-2 text-[11px] text-emerald-700 font-semibold">Featured until {new Date(r.expires_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</div>
                                    )}
                                    {r.status === "rejected" && r.rejected_reason && (
                                        <div className="mt-2 text-[11px] text-red-700">Reason: {r.rejected_reason}</div>
                                    )}
                                </div>
                                {r.status === "pending" && (
                                    <div className="flex flex-col gap-2 shrink-0">
                                        <button
                                            onClick={() => approve(r.id)}
                                            disabled={busy === r.id}
                                            data-testid={`featured-req-approve-${r.id}`}
                                            className="px-4 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-extrabold hover:bg-emerald-500 disabled:opacity-50"
                                        >
                                            {busy === r.id ? "…" : "Approve Feature"}
                                        </button>
                                        <button
                                            onClick={() => reject(r.id)}
                                            disabled={busy === r.id}
                                            data-testid={`featured-req-reject-${r.id}`}
                                            className="px-4 py-1.5 rounded-md bg-white border border-red-400 text-red-700 text-xs font-extrabold hover:bg-red-50 disabled:opacity-50"
                                        >
                                            Reject
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function StatSmall({ label, value, testid }) {
    return (
        <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-navy-500/50 font-bold">{label}</div>
            <div className="text-navy-500 text-sm font-bold truncate" data-testid={testid}>{value}</div>
        </div>
    );
}


/* ---------------- SETTINGS ---------------- */
function SettingsView() {
    const [sending, setSending] = useState(false);
    const [settings, setSettings] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loadingSettings, setLoadingSettings] = useState(true);

    useEffect(() => {
        api.get("/admin/platform-settings")
            .then((r) => setSettings(r.data))
            .catch(() => toast.error("Failed to load platform settings"))
            .finally(() => setLoadingSettings(false));
    }, []);

    const update = (patch) => setSettings((s) => ({ ...s, ...patch }));

    const saveSettings = async () => {
        if (!settings) return;
        setSaving(true);
        try {
            const r = await api.put("/admin/platform-settings", settings);
            setSettings(r.data);
            toast.success("Platform settings saved");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to save settings");
        } finally {
            setSaving(false);
        }
    };

    const sendDigest = async () => {
        if (!window.confirm("Send daily digest email to all verified subscribers now?")) return;
        setSending(true);
        try {
            const r = await api.post("/admin/digest/run-now");
            const d = r.data || {};
            if (d.reason === "no_new_listings" || d.reason === "no_new_properties") {
                toast.info("No new properties or auctions in the last 24 hours — digest skipped.");
            } else {
                const parts = [];
                if (d.properties) parts.push(`${d.properties} ${d.properties === 1 ? "property" : "properties"}`);
                if (d.auctions) parts.push(`${d.auctions} ${d.auctions === 1 ? "auction" : "auctions"}`);
                toast.success(`Digest sent to ${d.sent} subscribers (${parts.join(" + ") || "0 listings"})`);
            }
        } catch (e) {
            toast.error("Failed to send digest");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="space-y-6">
            <Header title="Platform Settings" subtitle="Manage your marketplace configuration in real-time." />

            {/* Daily Digest card */}
            <div className="rounded-xl bg-navy-900 border border-gold-500/30 p-6" data-testid="admin-digest-card">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <div className="font-heading font-bold text-white text-lg mb-1">Daily Digest Email</div>
                        <div className="text-sm text-white/65">Automatically runs daily at 09:00 IST. Sends a summary of new properties (last 24h) to every verified subscriber.</div>
                    </div>
                    <button
                        onClick={sendDigest}
                        disabled={sending}
                        data-testid="admin-send-digest-btn"
                        className="px-5 py-2.5 rounded-lg bg-gold-500 text-navy-900 font-bold text-sm hover:bg-gold-100 disabled:opacity-60 shrink-0"
                    >
                        {sending ? "Sending…" : "Send Digest Now"}
                    </button>
                </div>
            </div>

            {loadingSettings && (
                <div className="rounded-xl bg-navy-900 border border-white/5 p-8 text-center text-white/50">Loading platform settings…</div>
            )}

            {settings && !loadingSettings && (
                <>
                    {/* Branding & Contact */}
                    <SettingsCard title="Site Branding & Contact" subtitle="Public info shown across the marketplace">
                        <TextField label="Site Name" value={settings.site_name} onChange={(v) => update({ site_name: v })} testid="setting-site-name" />
                        <TextField label="Tagline" value={settings.tagline} onChange={(v) => update({ tagline: v })} testid="setting-tagline" />
                        <TextField label="Support Email" value={settings.support_email} onChange={(v) => update({ support_email: v })} testid="setting-support-email" />
                        <TextField label="Support Phone" value={settings.support_phone} onChange={(v) => update({ support_phone: v })} testid="setting-support-phone" />
                        <TextField label="WhatsApp Number" value={settings.whatsapp_number} onChange={(v) => update({ whatsapp_number: v })} testid="setting-whatsapp" />
                        <TextField label="Office Address" value={settings.office_address} onChange={(v) => update({ office_address: v })} testid="setting-address" />
                        <TextField label="Footer Note" value={settings.footer_note} onChange={(v) => update({ footer_note: v })} testid="setting-footer" span2 />
                        <TextField label="Hero Banner Text (leave empty to hide)" value={settings.hero_banner_text} onChange={(v) => update({ hero_banner_text: v })} testid="setting-hero-banner" span2 />
                    </SettingsCard>

                    {/* Chat Controls */}
                    <SettingsCard title="Chat & Community" subtitle="Turn features on or off for your users">
                        <ToggleField
                            label="Enable 1-on-1 Chat"
                            desc="Let paid subscribers message brokers, agents & builders privately."
                            checked={settings.chat_enabled}
                            onChange={(v) => update({ chat_enabled: v })}
                            testid="setting-chat-toggle"
                        />
                        <ToggleField
                            label="Enable Community Room"
                            desc="Public group chat for all paid subscribers."
                            checked={settings.community_room_enabled}
                            onChange={(v) => update({ community_room_enabled: v })}
                            testid="setting-community-toggle"
                        />
                    </SettingsCard>

                    {/* Property Rules */}
                    <SettingsCard title="Property Listing Rules" subtitle="Trial limits & moderation">
                        <NumberField
                            label="Free Property Listings per User"
                            desc="How many properties a non-paid user can post. Set 0 to require a subscription immediately."
                            value={settings.free_property_limit}
                            onChange={(v) => update({ free_property_limit: v })}
                            min={0}
                            max={20}
                            testid="setting-free-limit"
                        />
                        <ToggleField
                            label="Auto-approve new listings"
                            desc="Off = every listing goes to pending review before it appears in search."
                            checked={settings.auto_approve_properties}
                            onChange={(v) => update({ auto_approve_properties: v })}
                            testid="setting-auto-approve"
                        />
                        <NumberField
                            label="Referral Reward %"
                            desc="Commission % credited on referred property deals."
                            value={settings.referral_reward_percent}
                            onChange={(v) => update({ referral_reward_percent: v })}
                            min={0}
                            max={100}
                            testid="setting-referral-percent"
                        />
                    </SettingsCard>

                    {/* Tracking */}
                    <SettingsCard title="Marketing & Tracking" subtitle="Analytics pixels">
                        <ToggleField
                            label="Meta (Facebook) Pixel Tracking"
                            desc="Fire PageView, Search, Registration and Contact events on the client."
                            checked={settings.meta_pixel_enabled}
                            onChange={(v) => update({ meta_pixel_enabled: v })}
                            testid="setting-meta-pixel"
                        />
                    </SettingsCard>

                    {/* Maintenance */}
                    <SettingsCard title="Maintenance Mode" subtitle="Show a friendly holding page to visitors">
                        <ToggleField
                            label="Enable Maintenance Mode"
                            desc="Public pages show your maintenance message. Admins can still log in."
                            checked={settings.maintenance_mode}
                            onChange={(v) => update({ maintenance_mode: v })}
                            testid="setting-maintenance-toggle"
                        />
                        <TextField
                            label="Maintenance Message"
                            value={settings.maintenance_message}
                            onChange={(v) => update({ maintenance_message: v })}
                            testid="setting-maintenance-msg"
                            span2
                        />
                    </SettingsCard>

                    {/* Sticky Save Bar */}
                    <div className="sticky bottom-0 -mx-4 sm:mx-0 rounded-none sm:rounded-xl bg-navy-950/95 backdrop-blur border-t sm:border border-gold-500/40 px-5 py-4 flex items-center justify-between gap-4 shadow-2xl">
                        <div className="text-xs text-white/60">Changes apply instantly across the platform.</div>
                        <button
                            onClick={saveSettings}
                            disabled={saving}
                            data-testid="admin-save-settings-btn"
                            className="px-6 py-2.5 rounded-lg bg-gold-500 text-navy-900 font-extrabold text-sm hover:bg-gold-400 disabled:opacity-60"
                        >
                            {saving ? "Saving…" : "Save All Settings"}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

function SettingsCard({ title, subtitle, children }) {
    return (
        <div className="rounded-xl bg-navy-900 border border-white/5 p-5 sm:p-6">
            <div className="mb-4">
                <div className="font-heading font-bold text-white text-lg">{title}</div>
                {subtitle && <div className="text-xs text-white/55 mt-0.5">{subtitle}</div>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
        </div>
    );
}

function TextField({ label, value, onChange, testid, span2 }) {
    return (
        <label className={`block ${span2 ? "md:col-span-2" : ""}`}>
            <div className="text-[11px] uppercase tracking-wider text-white/50 mb-1.5">{label}</div>
            <input
                type="text"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full bg-navy-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gold-500/60"
            />
        </label>
    );
}

function NumberField({ label, desc, value, onChange, min = 0, max = 999, testid }) {
    return (
        <label className="block">
            <div className="text-[11px] uppercase tracking-wider text-white/50 mb-1.5">{label}</div>
            <input
                type="number"
                min={min}
                max={max}
                value={value ?? 0}
                onChange={(e) => onChange(parseInt(e.target.value || "0", 10))}
                data-testid={testid}
                className="w-full bg-navy-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gold-500/60"
            />
            {desc && <div className="text-[11px] text-white/45 mt-1.5">{desc}</div>}
        </label>
    );
}

function ToggleField({ label, desc, checked, onChange, testid }) {
    return (
        <button
            type="button"
            onClick={() => onChange(!checked)}
            data-testid={testid}
            className="text-left flex items-start justify-between gap-3 p-4 rounded-lg bg-navy-950 border border-white/10 hover:border-gold-500/40 transition-colors"
        >
            <div className="flex-1">
                <div className="font-bold text-white text-sm">{label}</div>
                {desc && <div className="text-[11px] text-white/50 mt-1 leading-relaxed">{desc}</div>}
            </div>
            <div
                className={`shrink-0 mt-0.5 w-11 h-6 rounded-full relative transition-colors ${checked ? "bg-gold-500" : "bg-navy-500/25 border border-navy-500/30"}`}
            >
                <div
                    className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${checked ? "translate-x-[22px]" : "translate-x-0.5"}`}
                />
            </div>
        </button>
    );
}

/* ---------------- AUCTIONS ---------------- */
function AuctionsAdmin() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);

    const reload = () => api.get("/admin/auctions").then((r) => setItems(r.data || []));
    useEffect(() => { reload(); }, []);

    const del = async (id) => {
        if (!window.confirm("Delete this auction and all its bids?")) return;
        await api.delete(`/admin/auctions/${id}`);
        toast.success("Deleted");
        reload();
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <Header title="Auctions" subtitle={`${items.length} auction${items.length !== 1 ? "s" : ""} — bank-attached, SARFAESI, court-ordered.`} />
                <button
                    onClick={() => setCreating(true)}
                    data-testid="admin-add-auction-btn"
                    className="px-4 py-2.5 rounded-md bg-gold-500 text-white font-semibold text-sm hover:bg-gold-600 flex items-center gap-2 self-start"
                >
                    <Plus className="w-4 h-4" /> Add Auction
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {items.map((a) => (
                    <div key={a.id} className="rounded-xl bg-navy-900 border border-white/5 hover:border-gold-500/40 p-4 flex gap-4" data-testid={`admin-auction-${a.id}`}>
                        <div className="w-32 aspect-[4/3] rounded-md overflow-hidden bg-navy-800 shrink-0">
                            {a.images?.[0] && <img src={fileUrl(a.images[0])} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                                <Link to={`/auctions/${a.id}`} target="_blank" className="font-heading font-bold text-white hover:text-gold-500 truncate block">{a.title}</Link>
                                <StatusPill status={a.status} />
                            </div>
                            <div className="text-xs text-white/55 mt-1 flex items-center gap-3 flex-wrap">
                                <span>{a.city} · {a.property_type}</span>
                                <span className="text-gold-500">{a.bid_count || 0} bids</span>
                            </div>
                            <div className="mt-2 text-sm">
                                <span className="text-white/55">Base: </span><span className="text-white font-semibold">{formatINR(a.base_price)}</span>
                                <span className="text-white/55 ml-3">Current: </span><span className="text-gold-500 font-bold">{formatINR(a.current_price)}</span>
                            </div>
                            <div className="text-[11px] text-white/50 mt-1 flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {new Date(a.start_time).toLocaleDateString()} → {new Date(a.end_time).toLocaleDateString()}
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3">
                                <button onClick={() => setEditing(a)} className="text-xs px-3 py-1.5 rounded border border-white/15 text-white/80 hover:border-gold-500/40 hover:text-gold-500 flex items-center gap-1">
                                    <Edit3 className="w-3 h-3" /> Edit
                                </button>
                                <button onClick={() => del(a.id)} className="text-xs px-3 py-1.5 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10">Delete</button>
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <div className="lg:col-span-2 text-center py-12 text-white/50 rounded-xl bg-navy-900 border border-white/5">No auctions. Click "Add Auction" to create one.</div>}
            </div>

            <AuctionFormModal open={creating} onClose={() => setCreating(false)} auction={null} onSaved={reload} />
            <AuctionFormModal open={!!editing} onClose={() => setEditing(null)} auction={editing} onSaved={reload} />
        </div>
    );
}

function StatusPill({ status }) {
    if (status === "live") return <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500 text-white font-bold shrink-0 animate-pulse">Live</span>;
    if (status === "upcoming") return <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-gold-500 text-white font-bold shrink-0">Upcoming</span>;
    return <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-navy-700 text-white/80 font-bold shrink-0">Ended</span>;
}

function AuctionFormModal({ open, onClose, auction, onSaved }) {
    const isEdit = !!auction;
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        const toLocal = (iso) => iso ? new Date(iso).toISOString().slice(0, 16) : "";
        setForm({
            title: auction?.title || "",
            description: auction?.description || "",
            property_type: auction?.property_type || "residential",
            subtype: auction?.subtype || "",
            area_sqft: auction?.area_sqft || "",
            bhk: auction?.bhk || "",
            city: auction?.city || "",
            state: auction?.state || "Maharashtra",
            locality: auction?.locality || "",
            address: auction?.address || "",
            images: auction?.images || [],
            amenities: auction?.amenities || [],
            base_price: auction?.base_price || "",
            bid_increment: auction?.bid_increment || 50000,
            reserve_price: auction?.reserve_price || "",
            start_time: toLocal(auction?.start_time),
            end_time: toLocal(auction?.end_time),
        });
    }, [auction, open]);

    const upload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            const urls = [];
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/properties/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                urls.push(data.url);
            }
            setForm((p) => ({ ...p, images: [...(p.images || []), ...urls] }));
            toast.success(`${files.length} image${files.length > 1 ? "s" : ""} uploaded`);
        } catch (err) {
            toast.error("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const toIso = (local) => local ? new Date(local).toISOString() : null;
            const payload = {
                ...form,
                base_price: Number(form.base_price),
                bid_increment: Number(form.bid_increment),
                reserve_price: form.reserve_price === "" ? null : Number(form.reserve_price),
                area_sqft: form.area_sqft === "" ? null : Number(form.area_sqft),
                bhk: form.bhk === "" ? null : Number(form.bhk),
                start_time: new Date(form.start_time).toISOString(),
                end_time: new Date(form.end_time).toISOString(),
                // Bank auction
                borrower_name: form.borrower_name || null,
                bank_name: form.bank_name || null,
                branch: form.branch || null,
                auction_type: form.auction_type || null,
                possession_type: form.possession_type || null,
                emd_amount: form.emd_amount === "" ? null : Number(form.emd_amount),
                emd_submission_deadline: toIso(form.emd_submission_deadline),
                inspection_date: toIso(form.inspection_date),
                contact_person: form.contact_person || null,
                contact_phone: form.contact_phone || null,
            };
            if (isEdit) {
                await api.put(`/admin/auctions/${auction.id}`, payload);
                toast.success("Auction updated");
            } else {
                await api.post("/admin/auctions", payload);
                toast.success("Auction created");
            }
            onSaved?.();
            onClose();
        } catch (err) {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;
    return createPortal(
        <div className="fixed inset-0 z-[9999] overflow-y-auto overscroll-contain bg-black/70 backdrop-blur-sm" onClick={onClose}>
            <div className="min-h-full flex items-center justify-center p-4">
                <div className="w-full max-w-4xl my-4 rounded-2xl bg-navy-900 border border-gold-500/30 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                    <div className="sticky top-0 bg-navy-900 z-10 px-6 py-4 border-b border-white/5 flex items-center justify-between rounded-t-2xl">
                        <h3 className="font-heading text-xl font-bold text-white">{isEdit ? `Edit · ${auction.title}` : "Add New Auction"}</h3>
                        <button onClick={onClose} className="text-white/50 hover:text-white p-1"><X className="w-5 h-5" /></button>
                    </div>
                <form onSubmit={submit} className="p-6 space-y-4" data-testid="auction-form">
                    <AField label="Title" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
                    <AField label="Description" textarea required value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <ASelect label="Type" value={form.property_type} onChange={(v) => setForm({ ...form, property_type: v })}
                            options={[{v:"residential",l:"Residential"},{v:"commercial",l:"Commercial"},{v:"plot",l:"Plot"},{v:"farmland",l:"Farmland"}]} />
                        <AField label="Subtype" value={form.subtype} onChange={(v) => setForm({ ...form, subtype: v })} placeholder="apartment, villa..." />
                        <AField label="BHK" type="number" value={form.bhk} onChange={(v) => setForm({ ...form, bhk: v })} />
                        <AField label="Area (sqft)" type="number" value={form.area_sqft} onChange={(v) => setForm({ ...form, area_sqft: v })} />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <AField label="City" required value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
                        <AField label="State" required value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
                        <AField label="Locality" value={form.locality} onChange={(v) => setForm({ ...form, locality: v })} />
                        <AField label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <AField label="Base Price (₹)" type="number" required value={form.base_price} onChange={(v) => setForm({ ...form, base_price: v })} />
                        <AField label="Bid Increment (₹)" type="number" required value={form.bid_increment} onChange={(v) => setForm({ ...form, bid_increment: v })} />
                        <AField label="Reserve Price (₹)" type="number" value={form.reserve_price} onChange={(v) => setForm({ ...form, reserve_price: v })} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <AField label="Start time" type="datetime-local" required value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} />
                        <AField label="End time" type="datetime-local" required value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} />
                    </div>

                    {/* Bank Auction Information (optional) */}
                    <div className="pt-4 border-t border-white/5">
                        <div className="text-xs uppercase tracking-[0.18em] text-gold-500 font-bold mb-3">Bank Auction Information (optional)</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <AField label="Borrower Name" value={form.borrower_name} onChange={(v) => setForm({ ...form, borrower_name: v })} placeholder="e.g. Sharma Industries Pvt Ltd" />
                            <AField label="Bank / Lender" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} placeholder="e.g. State Bank of India" />
                            <AField label="Branch" value={form.branch} onChange={(v) => setForm({ ...form, branch: v })} />
                            <ASelect label="Auction Type" value={form.auction_type} onChange={(v) => setForm({ ...form, auction_type: v })}
                                options={[{v:"",l:"—"},{v:"SARFAESI",l:"SARFAESI"},{v:"DRT",l:"DRT"},{v:"Court Order",l:"Court Order"},{v:"Bank Direct",l:"Bank Direct"}]} />
                            <ASelect label="Possession" value={form.possession_type} onChange={(v) => setForm({ ...form, possession_type: v })}
                                options={[{v:"",l:"—"},{v:"physical",l:"Physical"},{v:"symbolic",l:"Symbolic"}]} />
                            <AField label="EMD Amount (₹)" type="number" value={form.emd_amount} onChange={(v) => setForm({ ...form, emd_amount: v })} />
                            <AField label="EMD Submission Deadline" type="datetime-local" value={form.emd_submission_deadline} onChange={(v) => setForm({ ...form, emd_submission_deadline: v })} />
                            <AField label="Inspection Date" type="datetime-local" value={form.inspection_date} onChange={(v) => setForm({ ...form, inspection_date: v })} />
                            <AField label="Contact Person" value={form.contact_person} onChange={(v) => setForm({ ...form, contact_person: v })} />
                            <AField label="Contact Phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} />
                        </div>
                    </div>

                    <div>
                        <label className="text-xs text-white/60 mb-2 block">Photos</label>
                        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                            {form.images?.map((img, i) => (
                                <div key={i} className="relative aspect-square rounded-md overflow-hidden bg-navy-950 border border-white/10">
                                    <img src={img.startsWith("http") ? img : (process.env.REACT_APP_BACKEND_URL + img)} alt="" className="w-full h-full object-cover" />
                                    <button type="button" onClick={() => setForm((p) => ({ ...p, images: p.images.filter((_, idx) => idx !== i) }))} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-navy-900/90 text-white hover:bg-red-500 flex items-center justify-center">
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                            <label className="aspect-square rounded-md border-2 border-dashed border-white/15 hover:border-gold-500/60 cursor-pointer flex items-center justify-center text-white/60 text-xs">
                                {uploading ? "…" : "+ Upload"}
                                <input type="file" multiple accept="image/*" onChange={upload} className="hidden" />
                            </label>
                        </div>
                    </div>
                    <div className="flex gap-3 pt-2 border-t border-white/5">
                        <button type="submit" disabled={saving} className="flex-1 px-5 py-3 rounded-md bg-gold-500 text-white font-semibold hover:bg-gold-600 disabled:opacity-60">
                            {saving ? "Saving…" : (isEdit ? "Save Changes" : "Create Auction")}
                        </button>
                        <button type="button" onClick={onClose} className="px-5 py-3 rounded-md border border-white/15 text-white/80 hover:border-white/30">Cancel</button>
                    </div>
                    </form>
                </div>
            </div>
        </div>,
        document.body
    );
}

function AField({ label, value, onChange, type = "text", required, placeholder, textarea }) {
    return (
        <div>
            <label className="text-xs text-white/60 mb-1 block">{label}</label>
            {textarea ? (
                <textarea required={required} rows={3} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-navy-950 border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
            ) : (
                <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-navy-950 border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
            )}
        </div>
    );
}

function ASelect({ label, value, onChange, options }) {
    return (
        <div>
            <label className="text-xs text-white/60 mb-1 block">{label}</label>
            <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-navy-950 border border-white/10 rounded-md px-3 py-2 text-sm text-white">
                {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
        </div>
    );
}

/* ---------------- Helpers ---------------- */
function Header({ title, subtitle }) {
    return (
        <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold text-white">{title}</h1>
            {subtitle && <p className="text-white/55 text-sm mt-1">{subtitle}</p>}
        </div>
    );
}

function Stat({ icon: Icon, label, value, accent }) {
    return (
        <div className={`rounded-xl border p-5 ${accent ? "bg-gold-500/10 border-gold-500/40" : "bg-navy-900 border-white/5"}`}>
            <Icon className={`w-5 h-5 ${accent ? "text-gold-500" : "text-gold-500"}`} />
            <div className="font-heading text-3xl font-bold text-white mt-3">{value ?? "—"}</div>
            <div className="text-[11px] uppercase tracking-wider text-white/50 mt-1">{label}</div>
        </div>
    );
}

function Panel({ title, children }) {
    return (
        <div className="rounded-2xl bg-navy-900 border border-white/5 p-6">
            <div className="label-overline mb-4">{title}</div>
            {children}
        </div>
    );
}


/* ---------------- KYC REVIEW ---------------- */
function KYCReviewTable() {
    const [docs, setDocs] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(true);
    const [reviewing, setReviewing] = useState(null);
    const [notes, setNotes] = useState("");

    const reload = () => {
        setLoading(true);
        const url = filter === "all" ? "/admin/kyc" : `/admin/kyc?status=${filter}`;
        api.get(url).then((r) => setDocs(r.data || [])).finally(() => setLoading(false));
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

    const decide = async (doc, status) => {
        try {
            await api.put(`/admin/kyc/${doc.id}/review`, { status, review_notes: notes || null });
            toast.success(`KYC ${status}`);
            setReviewing(null);
            setNotes("");
            reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="space-y-6">
            <Header title="KYC Review Queue" subtitle="Verify partner documents and unlock verified badges." />
            <div className="flex flex-wrap gap-2" data-testid="admin-kyc-filters">
                {["pending", "approved", "rejected", "all"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setFilter(s)}
                        data-testid={`admin-kyc-filter-${s}`}
                        className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border transition-colors ${
                            filter === s ? "bg-gold-500 text-white border-gold-500" : "border-white/15 text-white/70 hover:border-gold-500/50"
                        }`}
                    >{s}</button>
                ))}
            </div>

            {loading ? (
                <div className="text-white/60 text-sm">Loading…</div>
            ) : docs.length === 0 ? (
                <div className="text-center py-12 rounded-xl bg-navy-900 border border-white/5 text-white/50">No KYC submissions in this view.</div>
            ) : (
                <div className="space-y-3">
                    {docs.map((d) => (
                        <div key={d.id} className="rounded-xl bg-navy-900 border border-white/5 p-5" data-testid={`admin-kyc-${d.id}`}>
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className="font-heading font-bold text-white">{d.user_name}</div>
                                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-gold-500/10 text-gold-500 border border-gold-500/30 capitalize">{d.user_role}</span>
                                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                                            d.status === "approved" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" :
                                            d.status === "rejected" ? "bg-red-500/10 text-red-400 border-red-500/30" :
                                            "bg-amber-500/10 text-amber-400 border-amber-500/30"
                                        }`}>{d.status}</span>
                                    </div>
                                    <div className="text-xs text-white/55 mt-1">{d.user_email}</div>
                                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                        <div>
                                            <div className="text-white/45 uppercase tracking-wider">Doc type</div>
                                            <div className="text-white capitalize mt-0.5">{(d.document_type || "").replace(/_/g, " ")}</div>
                                        </div>
                                        <div>
                                            <div className="text-white/45 uppercase tracking-wider">Doc number</div>
                                            <div className="text-white mt-0.5">{d.doc_number || "—"}</div>
                                        </div>
                                        <div>
                                            <div className="text-white/45 uppercase tracking-wider">Submitted</div>
                                            <div className="text-white mt-0.5">{new Date(d.created_at).toLocaleString()}</div>
                                        </div>
                                        <div>
                                            <a href={fileUrl(d.document_url)} target="_blank" rel="noreferrer" className="text-gold-500 hover:underline" data-testid={`admin-kyc-view-${d.id}`}>View document →</a>
                                        </div>
                                    </div>
                                    {d.notes && <div className="text-xs italic text-white/55 mt-2">User note: "{d.notes}"</div>}
                                    {d.review_notes && <div className="text-xs italic text-white/70 mt-1">Admin: "{d.review_notes}"</div>}
                                </div>

                                {d.status === "pending" && (
                                    <div className="flex flex-col gap-2 lg:items-end">
                                        {reviewing === d.id ? (
                                            <div className="space-y-2">
                                                <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`admin-kyc-notes-${d.id}`} placeholder="Review notes (optional)" className="w-full lg:w-72 bg-navy-950 border border-white/10 rounded-md px-3 py-2 text-xs text-white placeholder:text-white/40" />
                                                <div className="flex gap-2">
                                                    <button onClick={() => decide(d, "approved")} data-testid={`admin-kyc-approve-${d.id}`} className="flex-1 lg:flex-none px-4 py-2 rounded-md bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600">Approve</button>
                                                    <button onClick={() => decide(d, "rejected")} data-testid={`admin-kyc-reject-${d.id}`} className="flex-1 lg:flex-none px-4 py-2 rounded-md bg-red-500 text-white text-sm font-semibold hover:bg-red-600">Reject</button>
                                                    <button onClick={() => { setReviewing(null); setNotes(""); }} className="px-3 py-2 rounded-md border border-white/15 text-white/70 text-sm">Cancel</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button onClick={() => setReviewing(d.id)} data-testid={`admin-kyc-review-${d.id}`} className="px-5 py-2 rounded-md bg-gold-500 text-navy-900 font-semibold text-sm hover:bg-gold-100">Review</button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ---------------- WITHDRAWALS ---------------- */
function WithdrawalsTable() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(true);

    const reload = () => {
        setLoading(true);
        const url = filter === "all" ? "/admin/withdrawals" : `/admin/withdrawals?status=${filter}`;
        api.get(url).then((r) => setItems(r.data || [])).finally(() => setLoading(false));
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

    const decide = async (wd, status) => {
        const note = window.prompt(`${status === "completed" ? "Payment reference" : "Reject reason"} (optional)`) || null;
        try {
            await api.put(`/admin/withdrawals/${wd.id}`, { status, admin_notes: note });
            toast.success(`Withdrawal ${status}`);
            reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="space-y-6">
            <Header title="Withdrawal Requests" subtitle="Approve or reject partner cash-out requests." />
            <div className="flex flex-wrap gap-2" data-testid="admin-wd-filters">
                {["pending", "completed", "rejected", "all"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setFilter(s)}
                        data-testid={`admin-wd-filter-${s}`}
                        className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border transition-colors ${
                            filter === s ? "bg-gold-500 text-white border-gold-500" : "border-white/15 text-white/70 hover:border-gold-500/50"
                        }`}
                    >{s}</button>
                ))}
            </div>

            {loading ? (
                <div className="text-white/60 text-sm">Loading…</div>
            ) : items.length === 0 ? (
                <div className="text-center py-12 rounded-xl bg-navy-900 border border-white/5 text-white/50">No withdrawals in this view.</div>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-white/5">
                    <table className="w-full text-sm">
                        <thead className="bg-navy-950 text-xs uppercase tracking-wider text-white/50">
                            <tr>
                                <th className="text-left px-4 py-3">User</th>
                                <th className="text-left px-4 py-3">UPI / Bank</th>
                                <th className="text-right px-4 py-3">Amount</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-left px-4 py-3">Created</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {items.map((w) => (
                                <tr key={w.id} className="bg-navy-900" data-testid={`admin-wd-${w.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold text-white">{w.user_name}</div>
                                        <div className="text-xs text-white/55">{w.user_email}</div>
                                    </td>
                                    <td className="px-4 py-3 text-white/80">{w.upi_or_bank}</td>
                                    <td className="px-4 py-3 text-right text-gold-500 font-bold">{formatINR(w.amount)}</td>
                                    <td className="px-4 py-3">
                                        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${
                                            w.status === "completed" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" :
                                            w.status === "rejected" ? "bg-red-500/10 text-red-400 border-red-500/30" :
                                            "bg-amber-500/10 text-amber-400 border-amber-500/30"
                                        }`}>{w.status}</span>
                                    </td>
                                    <td className="px-4 py-3 text-white/65 text-xs">{new Date(w.created_at).toLocaleString()}</td>
                                    <td className="px-4 py-3 text-right">
                                        {w.status === "pending" && (
                                            <div className="inline-flex gap-2">
                                                <button onClick={() => decide(w, "completed")} data-testid={`admin-wd-approve-${w.id}`} className="px-3 py-1.5 rounded border border-emerald-500/40 text-emerald-400 text-xs hover:bg-emerald-500/10">Mark Paid</button>
                                                <button onClick={() => decide(w, "rejected")} data-testid={`admin-wd-reject-${w.id}`} className="px-3 py-1.5 rounded border border-red-500/40 text-red-400 text-xs hover:bg-red-500/10">Reject</button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/* ------------------ GPM Partners Admin Panel ------------------ */
function PartnersPanel() {
    const [items, setItems] = React.useState([]);
    const [stats, setStats] = React.useState({});
    const [filter, setFilter] = React.useState("");
    const [q, setQ] = React.useState("");
    const [loading, setLoading] = React.useState(true);
    const [busyId, setBusyId] = React.useState(null);
    const [modal, setModal] = React.useState(null); // { type: 'view'|'edit'|'verify'|'message'|'delete', partner }

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter) params.status = filter;
            if (q.trim()) params.q = q.trim();
            const r = await api.get("/admin/partners", { params });
            setItems(r.data.items || []);
            setStats(r.data.stats || {});
        } catch (e) {
            toast.error("Could not load partners");
        }
        setLoading(false);
    }, [filter, q]);
    React.useEffect(() => { load(); }, [load]);

    const runStatus = async (uid, action) => {
        const reason = action === "suspend" ? window.prompt("Reason for suspension (optional):") : null;
        setBusyId(uid);
        try {
            await api.patch(`/admin/partners/${uid}`, { action, reason });
            toast.success(`Partner ${action}d`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Action failed");
        }
        setBusyId(null);
    };

    const filters = [
        { v: "", label: "All", n: (stats.active || 0) + (stats.expired || 0) + (stats.suspended || 0) + (stats.refunded || 0) + (stats.pending_payment || 0) },
        { v: "active", label: "Active", n: stats.active || 0 },
        { v: "pending_verification", label: "Pending Verification", n: stats.pending_verification || 0 },
        { v: "expired", label: "Expired", n: stats.expired || 0 },
        { v: "suspended", label: "Suspended", n: stats.suspended || 0 },
        { v: "refunded", label: "Refunded", n: stats.refunded || 0 },
        { v: "not_activated", label: "Pending Payment", n: stats.pending_payment || 0 },
    ];

    return (
        <div data-testid="admin-partners-panel">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold-500/90 font-extrabold">GPM Partner Program</div>
                    <h2 className="font-heading text-2xl font-bold text-white">Partner Management</h2>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search name / email / partner-id" className="h-9 flex-1 sm:w-64 px-3 rounded-md bg-navy-900 border border-white/10 text-white text-sm" data-testid="partners-search" />
                    <button onClick={load} className="h-9 px-3 rounded-md bg-gold-500 text-navy-950 text-xs font-bold" data-testid="partners-search-btn">Search</button>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
                {filters.map((f) => (
                    <button key={f.v} onClick={() => setFilter(f.v)} className={`px-3 py-1.5 rounded-full text-xs font-bold ${filter === f.v ? "bg-gold-500 text-navy-950" : "bg-white/5 text-white/70 border border-white/15 hover:border-gold-500/40"}`} data-testid={`partners-filter-${f.v || "all"}`}>{f.label} <span className="opacity-70">({f.n})</span></button>
                ))}
            </div>

            {loading ? (
                <div className="py-12 text-center text-white/60"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
            ) : items.length === 0 ? (
                <div className="py-12 text-center text-white/50 rounded-xl bg-navy-900 border border-white/5">No partners match this filter.</div>
            ) : (
                <>
                    {/* Mobile: stacked cards */}
                    <div className="grid gap-3 md:hidden" data-testid="partners-cards-mobile">
                        {items.map((p) => (
                            <PartnerCard key={p.id} p={p} busy={busyId === p.id}
                                onOpen={(type) => setModal({ type, partner: p })}
                                onStatus={(action) => runStatus(p.id, action)}
                            />
                        ))}
                    </div>

                    {/* Desktop: table */}
                    <div className="hidden md:block overflow-x-auto rounded-xl border border-white/10" data-testid="partners-table-desktop">
                        <table className="w-full text-sm">
                            <thead className="bg-navy-900 text-[10px] uppercase tracking-widest text-white/60 font-bold">
                                <tr>
                                    <th className="text-left px-3 py-2">Partner ID</th>
                                    <th className="text-left px-3 py-2">Name / Email</th>
                                    <th className="text-left px-3 py-2">Ref Code</th>
                                    <th className="text-left px-3 py-2">Status</th>
                                    <th className="text-left px-3 py-2">Verified</th>
                                    <th className="text-left px-3 py-2">Expires</th>
                                    <th className="text-right px-3 py-2 min-w-[220px]">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="text-white/85">
                                {items.map((p) => (
                                    <tr key={p.id} className="border-t border-white/5" data-testid={`partner-row-${p.id}`}>
                                        <td className="px-3 py-2 font-mono text-xs">{p.partner_id || "—"}</td>
                                        <td className="px-3 py-2">
                                            <div className="font-semibold">{p.name}</div>
                                            <div className="text-xs text-white/50">{p.email}</div>
                                        </td>
                                        <td className="px-3 py-2 font-mono text-xs text-gold-500">{p.referral_code}</td>
                                        <td className="px-3 py-2"><PartnerStatusPill status={p.partner_status} /></td>
                                        <td className="px-3 py-2 text-xs">{p.partner_verified ? <span className="text-emerald-400 font-bold">✓ Yes</span> : <span className="text-white/40">—</span>}</td>
                                        <td className="px-3 py-2 text-xs text-white/70">{p.partner_expires_at ? new Date(p.partner_expires_at).toLocaleDateString("en-IN") : "—"}</td>
                                        <td className="px-3 py-2">
                                            <div className="flex gap-1 justify-end flex-wrap">
                                                <ActionBtn label="View" testid={`view-${p.id}`} onClick={() => setModal({ type: "view", partner: p })} />
                                                <ActionBtn label="Edit" testid={`edit-${p.id}`} onClick={() => setModal({ type: "edit", partner: p })} />
                                                {!p.partner_verified && p.partner_status === "active" && (
                                                    <ActionBtn label="Verify" tone="emerald" testid={`verify-${p.id}`} onClick={() => setModal({ type: "verify", partner: p })} />
                                                )}
                                                <ActionBtn label="Message" testid={`message-${p.id}`} onClick={() => setModal({ type: "message", partner: p })} />
                                                {p.partner_status === "active" && <ActionBtn label="Suspend" tone="rose" testid={`suspend-${p.id}`} onClick={() => runStatus(p.id, "suspend")} />}
                                                {p.partner_status === "suspended" && <ActionBtn label="Activate" tone="emerald" testid={`activate-${p.id}`} onClick={() => runStatus(p.id, "activate")} />}
                                                <ActionBtn label="Delete" tone="rose" testid={`delete-${p.id}`} onClick={() => setModal({ type: "delete", partner: p })} />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {modal && (
                <PartnerModal
                    modal={modal}
                    onClose={() => setModal(null)}
                    onDone={() => { setModal(null); load(); }}
                />
            )}
        </div>
    );
}

function PartnerStatusPill({ status }) {
    const map = {
        active: "bg-emerald-500/20 text-emerald-300",
        expired: "bg-amber-500/20 text-amber-300",
        suspended: "bg-rose-500/20 text-rose-300",
        refunded: "bg-blue-500/20 text-blue-300",
        deactivated: "bg-white/10 text-white/50",
    };
    return <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase whitespace-nowrap ${map[status] || "bg-white/10 text-white/60"}`}>{status || "pending"}</span>;
}

function ActionBtn({ label, tone, onClick, testid }) {
    const toneClass = tone === "rose"
        ? "bg-rose-500/20 text-rose-300 hover:bg-rose-500/30"
        : tone === "emerald"
            ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
            : "bg-white/10 text-white/80 hover:bg-white/15";
    return <button onClick={onClick} data-testid={testid} className={`h-7 px-2.5 rounded text-[10px] font-bold uppercase tracking-wide ${toneClass}`}>{label}</button>;
}

function PartnerCard({ p, busy, onOpen, onStatus }) {
    const [open, setOpen] = React.useState(false);
    return (
        <div className="rounded-xl border border-white/10 bg-navy-900 p-4" data-testid={`partner-card-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="font-semibold text-white truncate">{p.name}</div>
                    <div className="text-[11px] text-white/50 truncate">{p.email}</div>
                    <div className="mt-1 flex flex-wrap gap-1.5 items-center">
                        <span className="font-mono text-[10px] text-gold-500">{p.partner_id || "—"}</span>
                        <PartnerStatusPill status={p.partner_status} />
                        {p.partner_verified && <span className="text-[10px] font-bold text-emerald-400">✓ Verified</span>}
                    </div>
                </div>
                <button onClick={() => setOpen((o) => !o)} data-testid={`actions-menu-${p.id}`} className="h-8 w-8 flex items-center justify-center rounded-md bg-white/5 hover:bg-white/10 text-white shrink-0">
                    <MoreVertical className="w-4 h-4" />
                </button>
            </div>
            {open && (
                <div className="mt-3 grid grid-cols-2 gap-1.5 border-t border-white/10 pt-3" data-testid={`actions-open-${p.id}`}>
                    <MobileMenuItem label="View" onClick={() => { setOpen(false); onOpen("view"); }} />
                    <MobileMenuItem label="Edit" onClick={() => { setOpen(false); onOpen("edit"); }} />
                    {!p.partner_verified && p.partner_status === "active" && <MobileMenuItem label="Verify" tone="emerald" onClick={() => { setOpen(false); onOpen("verify"); }} />}
                    <MobileMenuItem label="Message" onClick={() => { setOpen(false); onOpen("message"); }} />
                    {p.partner_status === "active" && <MobileMenuItem label="Suspend" tone="rose" onClick={() => { setOpen(false); onStatus("suspend"); }} />}
                    {p.partner_status === "suspended" && <MobileMenuItem label="Activate" tone="emerald" onClick={() => { setOpen(false); onStatus("activate"); }} />}
                    <MobileMenuItem label="Delete" tone="rose" onClick={() => { setOpen(false); onOpen("delete"); }} />
                </div>
            )}
        </div>
    );
}

function MobileMenuItem({ label, tone, onClick }) {
    const toneClass = tone === "rose" ? "text-rose-300" : tone === "emerald" ? "text-emerald-300" : "text-white/85";
    return <button onClick={onClick} className={`h-9 px-3 rounded-md text-xs font-bold bg-white/5 hover:bg-white/10 ${toneClass}`}>{label}</button>;
}

/* ------------- Partner action modals ------------- */
function PartnerModal({ modal, onClose, onDone }) {
    const { type, partner } = modal;
    const [detail, setDetail] = React.useState(null);
    const [saving, setSaving] = React.useState(false);
    // form state
    const [form, setForm] = React.useState({
        name: partner.name || "",
        phone: partner.phone || "",
        city: partner.city || "",
        partner_expires_at: partner.partner_expires_at ? partner.partner_expires_at.slice(0, 10) : "",
        admin_notes: partner.admin_notes || "",
    });
    const [msg, setMsg] = React.useState({ subject: "", body: "", channels: ["notification"] });

    React.useEffect(() => {
        if (type === "view") {
            api.get(`/admin/partners/${partner.id}`).then((r) => setDetail(r.data)).catch(() => setDetail({}));
        }
    }, [type, partner.id]);

    const submitEdit = async () => {
        setSaving(true);
        try {
            const payload = { ...form };
            if (payload.partner_expires_at) payload.partner_expires_at = new Date(payload.partner_expires_at).toISOString();
            const r = await api.patch(`/admin/partners/${partner.id}/profile`, payload);
            toast.success(`Updated: ${(r.data.updated_fields || []).join(", ") || "no changes"}`);
            onDone();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        setSaving(false);
    };
    const submitVerify = async () => {
        setSaving(true);
        try {
            await api.post(`/admin/partners/${partner.id}/verify`);
            toast.success("Partner verified");
            onDone();
        } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
        setSaving(false);
    };
    const submitMessage = async () => {
        if (!msg.subject.trim() || !msg.body.trim()) { toast.error("Subject and body are required"); return; }
        setSaving(true);
        try {
            const r = await api.post(`/admin/partners/${partner.id}/message`, msg);
            toast.success(`Message sent via ${(r.data.delivered || []).join(", ")}`);
            onDone();
        } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
        setSaving(false);
    };
    const submitDelete = async () => {
        setSaving(true);
        try {
            await api.delete(`/admin/partners/${partner.id}`);
            toast.success("Partner deactivated (soft delete)");
            onDone();
        } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose} data-testid={`partner-modal-${type}`}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-navy-950 border border-white/10 shadow-2xl">
                <div className="flex items-center justify-between p-5 border-b border-white/10">
                    <h3 className="font-heading text-lg font-bold text-white capitalize">{type} · {partner.name}</h3>
                    <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="modal-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-4">
                    {type === "view" && (!detail ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-gold-500" /> : (
                        <div className="grid gap-4 text-sm">
                            <Section title="Partner Profile">
                                <KV k="Partner ID" v={detail.profile?.partner_id} mono />
                                <KV k="Email" v={detail.profile?.email} />
                                <KV k="Phone" v={detail.profile?.phone} />
                                <KV k="City" v={detail.profile?.city} />
                                <KV k="Referral Code" v={detail.profile?.referral_code} mono />
                                <KV k="Status" v={detail.profile?.partner_status} />
                                <KV k="Verified" v={detail.profile?.partner_verified ? "Yes" : "No"} />
                                <KV k="Package" v={detail.profile?.partner_package_id} />
                                <KV k="Activated" v={detail.profile?.partner_activated_at?.slice(0, 10)} />
                                <KV k="Expires" v={detail.profile?.partner_expires_at?.slice(0, 10)} />
                            </Section>
                            <Section title="Referrals"><KV k="Total" v={detail.referrals?.total} /><KV k="Verified" v={detail.referrals?.verified} /><KV k="Pending" v={detail.referrals?.pending} /></Section>
                            <Section title="Business"><KV k="Total leads" v={detail.business?.total_leads} /><KV k="Leads this month" v={detail.business?.leads_this_month} /></Section>
                            <Section title="Commission (₹)">
                                <KV k="Pending" v={detail.commission?.pending?.toLocaleString?.("en-IN")} />
                                <KV k="Approved" v={detail.commission?.approved?.toLocaleString?.("en-IN")} />
                                <KV k="Paid" v={detail.commission?.paid?.toLocaleString?.("en-IN")} />
                                <KV k="Wallet Balance" v={detail.commission?.wallet_balance?.toLocaleString?.("en-IN")} />
                            </Section>
                            <Section title="Payouts (₹)">
                                <KV k="Pending" v={detail.payouts?.pending?.toLocaleString?.("en-IN")} />
                                <KV k="Paid" v={detail.payouts?.paid?.toLocaleString?.("en-IN")} />
                                <KV k="Failed" v={detail.payouts?.failed?.toLocaleString?.("en-IN")} />
                                <KV k="Reversed" v={detail.payouts?.reversed?.toLocaleString?.("en-IN")} />
                            </Section>
                            {detail.audit_trail?.length > 0 && (
                                <Section title={`Audit Trail (last ${detail.audit_trail.length})`}>
                                    <div className="col-span-2 max-h-40 overflow-y-auto text-xs space-y-1">
                                        {detail.audit_trail.map((a, i) => (
                                            <div key={i} className="flex justify-between gap-3 border-b border-white/5 py-1">
                                                <span className="text-white/85 font-mono">{a.action}</span>
                                                <span className="text-white/50">{a.admin_email}</span>
                                                <span className="text-white/40 whitespace-nowrap">{a.created_at?.slice(0, 16).replace("T", " ")}</span>
                                            </div>
                                        ))}
                                    </div>
                                </Section>
                            )}
                        </div>
                    ))}

                    {type === "edit" && (
                        <div className="space-y-3">
                            <p className="text-xs text-white/60">Financial history is protected — only profile fields can be edited here.</p>
                            <FField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="edit-name" />
                            <FField label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="edit-phone" />
                            <FField label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="edit-city" />
                            <FField label="Expires on (YYYY-MM-DD)" type="date" value={form.partner_expires_at} onChange={(v) => setForm({ ...form, partner_expires_at: v })} testid="edit-expiry" />
                            <FField label="Admin notes" textarea value={form.admin_notes} onChange={(v) => setForm({ ...form, admin_notes: v })} testid="edit-notes" />
                        </div>
                    )}

                    {type === "verify" && (
                        <div className="space-y-3">
                            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4">
                                <p className="text-sm text-white">Verify this Partner? This awards the site-wide "verified" badge and cannot be undone from this dialog. Only proceed after checking their KYC documents.</p>
                            </div>
                            <p className="text-xs text-white/60">Partner: <b className="text-white">{partner.name}</b> · {partner.email}</p>
                        </div>
                    )}

                    {type === "message" && (
                        <div className="space-y-3">
                            <FField label="Subject" value={msg.subject} onChange={(v) => setMsg({ ...msg, subject: v })} testid="msg-subject" />
                            <FField label="Body" textarea rows={6} value={msg.body} onChange={(v) => setMsg({ ...msg, body: v })} testid="msg-body" />
                            <div className="flex gap-4 text-xs">
                                {["notification", "email"].map((ch) => (
                                    <label key={ch} className="flex items-center gap-1.5 text-white/80" data-testid={`msg-channel-${ch}`}>
                                        <input type="checkbox" checked={msg.channels.includes(ch)} onChange={(e) => setMsg({ ...msg, channels: e.target.checked ? [...msg.channels, ch] : msg.channels.filter((x) => x !== ch) })} className="accent-gold-500" />
                                        <span className="capitalize">{ch}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {type === "delete" && (
                        <div className="space-y-3">
                            <div className="rounded-xl border border-rose-500/40 bg-rose-500/5 p-4">
                                <p className="text-sm text-white font-bold">Soft-delete this Partner?</p>
                                <ul className="text-xs text-white/70 mt-2 space-y-1 list-disc pl-5">
                                    <li>The user will lose access to their account (login blocked).</li>
                                    <li>Their partner_status will be set to <b>deactivated</b>.</li>
                                    <li>Referrals, commissions and payout history <b>are preserved</b> for accounting/audit.</li>
                                    <li>This does <b>NOT</b> physically remove financial records.</li>
                                </ul>
                            </div>
                            <p className="text-xs text-white/60">Partner: <b className="text-white">{partner.name}</b> · {partner.email}</p>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 p-5 border-t border-white/10">
                    <button onClick={onClose} className="h-9 px-4 rounded-md bg-white/10 text-white text-xs font-bold hover:bg-white/15" data-testid="modal-cancel">Cancel</button>
                    {type === "edit" && <button onClick={submitEdit} disabled={saving} className="h-9 px-4 rounded-md bg-gold-500 text-navy-950 text-xs font-bold disabled:opacity-50" data-testid="modal-save">{saving ? "Saving…" : "Save"}</button>}
                    {type === "verify" && <button onClick={submitVerify} disabled={saving} className="h-9 px-4 rounded-md bg-emerald-500 text-navy-950 text-xs font-bold disabled:opacity-50" data-testid="modal-verify">{saving ? "Verifying…" : "Verify Partner"}</button>}
                    {type === "message" && <button onClick={submitMessage} disabled={saving} className="h-9 px-4 rounded-md bg-gold-500 text-navy-950 text-xs font-bold disabled:opacity-50" data-testid="modal-send">{saving ? "Sending…" : "Send Message"}</button>}
                    {type === "delete" && <button onClick={submitDelete} disabled={saving} className="h-9 px-4 rounded-md bg-rose-500 text-white text-xs font-bold disabled:opacity-50" data-testid="modal-delete">{saving ? "Deleting…" : "Delete Partner"}</button>}
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-gold-500/90 font-bold mb-1">{title}</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg bg-navy-900 border border-white/5 p-3">{children}</div>
        </div>
    );
}

function KV({ k, v, mono }) {
    return (
        <>
            <span className="text-white/55 text-xs">{k}</span>
            <span className={`text-white text-xs text-right ${mono ? "font-mono" : ""}`}>{v ?? "—"}</span>
        </>
    );
}

function FField({ label, value, onChange, textarea, rows = 3, type = "text", testid }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-widest text-white/60 font-bold mb-1">{label}</span>
            {textarea
                ? <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-navy-900 border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
                : <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-navy-900 border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
            }
        </label>
    );
}

